import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, Chip, EmptyState, Input, Label, SearchField, Switch, TextField, ToggleButton, ToggleButtonGroup } from '@heroui/react'
import { reach, type Behind } from '../lib/designReach'
import { MAX_DESIGN_NAME, copyBase, copyNameWithin } from '../lib/copyName'
import { afterSave, answeredWithin, revisionFor, type Revision } from '../lib/draftRevision'
import { api, ApiError, type LayoutTemplate, type LayoutVersion, type StoredLayout } from '../lib/api'
import { EditorCanvas, EditorPalette } from '../layout/Editor'
import { LayerList, LeftPanel } from '../layout/LeftPanel'
import { DesignPanel } from '../layout/DesignPanel'
import { toggleHidden } from '../layout/look'
import { Inspector, PagePanel, Seg, WelcomePanel } from '../layout/Inspector'
import { IconButton, Notice, StudioModal, StudioSelect } from '../layout/heroParts'
import { resizeCanvas } from '../layout/canvasSize'
import { PageTabs } from '../layout/PageTabs'
import { CounterTabs } from '../layout/CounterTabs'
import { CalledPanel, DisplayTabs } from '../layout/DisplayTabs'
import { DisplayPreview } from '../layout/DisplayPreview'
import { addCalled } from '../layout/calledScreen'
import { counterStarts, drawnScreen, firstScreen } from '../layout/counter'
import { CounterPreview } from '../layout/CounterPreview'
import { CounterLoop } from '../layout/CounterStage'
import { MoreMenu } from '../layout/MoreMenu'
import { addPage, pageLabel, whereIs } from '../layout/pages'
import { addOpener } from '../layout/opener'
import { nameProblem, type NameProblem } from '../layout/names'
import { loadZoom, saveZoom, stepZoom, type Zoom } from '../layout/zoom'
import { pickBranch } from '../layout/branchPick'
import { branchesOf, designOrganisation } from '../layout/designOrg'
import { useSession } from '../ui/Session'
import { serviceName } from '../lib/kioskText'
import { KioskPreview, useBranchOffer } from '../layout/KioskPreview'
import { KIOSK_LOOKS, kioskFromServices, type KioskLook } from '../layout/kioskTemplates'
import { withoutGrids } from '../layout/gridButtons'
import { atSize, presetLabel, presetsFor } from '../layout/canvasSize'
import { matchBranch, matches } from '../lib/designMatch'
import { profileBranches, servicesOfProfile } from '../lib/kioskProfile'
import { designFor, layoutKindFor } from '../lib/branchDesigns'
import { LayoutSurface, type LayoutBindings } from '../layout/render'
import { previewBindings } from '../layout/sample'
import { NUDGE, NUDGE_FAR, elementName, faults as faultsOf, problems, sameDocument, warningFaults } from '../layout/editing'
import { collectIssues, issueByElement, type Issue } from '../layout/issues'
import { IssuesIndicator } from '../layout/IssuesIndicator'
import { MachineLinks } from '../layout/MachineLinks'
import { linkRows, linksOffered, type LinkRow } from '../layout/linkRows'
import { AssignRow, PlanNotice, UseBranches } from '../layout/UseBranches'
import { hasPlan, machineNoun, planOf, succeeded, toggleTick, type DesignBranch, type DesignResult, type Ticks } from '../lib/designBranches'
import { align, copies, distribute, moveMany, removeMany, toggleIn, type Alignment } from '../layout/multi'
import { copyElements, pasteClip, useClip } from '../layout/clipboard'
import { publishBlock, saveStatus, type PublishBlock, type SaveStatus } from '../layout/saveStatus'
import { saveQueue } from '../layout/saveQueue'
import { withMediaItem, withPictureAsset, type MediaTarget } from '../layout/media'
import {
  MENU_PAGE,
  pageIds,
  drawOrder,
  onScreen,
  textIn,
  type KioskScreen,
  type Screen,
  type LayoutDocument,
  type LayoutElement,
  type LayoutKind,
  type MediaItem,
} from '../layout/types'
import type { Branch, Identity, OperationDoc, OperationProfileRow, ProfileUnitKind, Service, Unit } from '../lib/types'
import { refusedForSignage } from '../lib/signageRefusals'
import { Chrome } from '../ui/Chrome'
import { Icon } from '../ui/icons'
import { Pager, usePaged } from '../ui/Pager'
import { useStaffText } from '../ui/StaffText'
import { explain } from '../lib/staffErrors'
import { ENGLISH_ONLY, formatMoment, hasPhrase, t, type StaffKey, type StaffLanguage, type Vars } from '../lib/staffText'
import { useCustomerLanguages } from '../lib/i18n/useCustomerLanguages'
import { nativeName } from '../lib/i18n/customer'
import { overflowingLanguages } from '../layout/render'
import { showToast } from '../ui/toast'
import '../styles/studio.css'
import '../styles/equipment.css'

/**
 * Designing the screens.
 *
 * Every design is in the organisation's library (0062). Machines choose it in
 * their unit settings (equipment profiles); here a design is only made the
 * default and, for a printed ticket, given services (Use). A kiosk design
 * belongs to one operation profile and offers only that profile's services.
 *
 * Three things happen in the editor and they are deliberately separate. Editing
 * changes a draft, which nobody outside this page can see. Publishing puts the
 * draft on every screen that uses the design. Restoring brings an old version back
 * into the draft, not onto the wall.
 *
 * The address says where you are — /screens, /screens#display for one group,
 * /screens#<design id> for one design — so a link from a branch's page opens the
 * design, and reload and Back work.
 */

const SAVE_AFTER_MS = 900
/** How long leaving a design waits for its last save before asking instead. */
const SETTLE_MS = 10_000

const KINDS: LayoutKind[] = ['display', 'counter', 'kiosk', 'ticket']

const KIND: Record<LayoutKind, { one: StaffKey; hint: StaffKey; machines: StaffKey }> = {
  display: { one: 'designs.page.kind.display', hint: 'designs.page.hint.display', machines: 'designs.page.screens' },
  kiosk: { one: 'designs.page.kind.kiosk', hint: 'designs.page.hint.kiosk', machines: 'designs.page.kiosks' },
  ticket: { one: 'designs.page.kind.ticket', hint: 'designs.page.hint.ticket', machines: 'designs.page.kiosks' },
  counter: { one: 'designs.page.kind.counter', hint: 'designs.page.hint.counter', machines: 'designs.page.screens' },
}

type Say = (key: StaffKey, vars?: Vars) => string

/** Operation profile documents by id: null until loaded; a document that failed to load is null. */
type ProfileDocs = Record<string, OperationDoc | null> | null

/** Where the page is: the landing, one group, or one design. */
type Route = { group?: LayoutKind; id?: string }

export function routeOf(hash: string): Route {
  let h = hash.replace(/^#/, '')
  try {
    h = decodeURIComponent(h)
  } catch {
    // A hand-typed address that does not decode is no address.
  }
  if (!h) return {}
  if ((KINDS as string[]).includes(h)) return { group: h as LayoutKind }
  return { id: h }
}

/** A design used by many branches names the first few on its card; the rest are a count. */
const SHOWN_BRANCHES = 2

export function Layouts({
  identity,
}: {
  identity: Identity
  onNavigate?: (to: string) => void
}) {
  const [layouts, setLayouts] = useState<StoredLayout[]>([])
  const [loaded, setLoaded] = useState(false)
  const [route, setRoute] = useState<Route>(() => routeOf(window.location.hash))
  // A design opened from another page: its Back returns there, not to the group.
  const fromElsewhere = useRef(Boolean(route.id))
  const [current, setCurrent] = useState<StoredLayout | null>(null)
  const [doc, setDoc] = useState<LayoutDocument | null>(null)
  // What is selected on the canvas, in the order it was chosen.
  const [selection, setSelection] = useState<string[]>([])
  const setSelectedId = useCallback((id: string | null) => setSelection(id ? [id] : []), [])
  // A kiosk design tried as a customer at one branch would meet it.
  const [previewing, setPreviewing] = useState(false)
  const openedId = current?.id
  useEffect(() => setPreviewing(false), [openedId])
  const [screen, setScreen] = useState<Screen>('attract')
  // Which kiosk menu page is being drawn on, when the screen is the choosing one.
  const [page, setPage] = useState<string>(MENU_PAGE)
  const [language, setLanguage] = useState(ENGLISH_ONLY ? 'en' : 'az')
  const [history, setHistory] = useState<LayoutVersion[]>([])
  const [showHistory, setShowHistory] = useState(false)
  const [publishing, setPublishing] = useState(false)
  // The design whose use (default, slip services) is being set: the open one, or one from the list.
  const [using, setUsing] = useState<StoredLayout | null>(null)
  const [deleting, setDeleting] = useState<StoredLayout | null>(null)
  const [renaming, setRenaming] = useState<StoredLayout | null>(null)
  const [duplicating, setDuplicating] = useState(false)
  const [showProblems, setShowProblems] = useState(false)
  const [error, setError] = useState('')
  const { say, lang } = useStaffText()
  // The owner's is the one chosen in the top bar (App remounts this page when it changes).
  const org = useSession()?.org
  const workingIn = designOrganisation({ chosen: org?.organisation, organisations: org?.organisations, own: identity.staff.tenantId })
  const [creating, setCreating] = useState(false)
  const [templates, setTemplates] = useState<Record<LayoutKind, LayoutTemplate[]> | null>(null)
  const [branches, setBranches] = useState<Branch[]>([])
  const [machines, setMachines] = useState<Unit[]>([])
  const [machinesKnown, setMachinesKnown] = useState(false)
  // The branch the list is narrowed to, and the one a design opened from it is
  // previewed at.
  const [branchFilter, setBranchFilter] = useState('')

  // Undo is a stack of whole documents. They are small — a few kilobytes — and a
  // list of edits to replay is a second model of the same thing that can disagree
  // with the first.
  const undoStack = useRef<LayoutDocument[]>([])
  const redoStack = useRef<LayoutDocument[]>([])
  const saveTimer = useRef<number | null>(null)
  // The save waiting for its timer, so leaving the editor can send it at once and
  // the gallery it returns to draws what was just drawn.
  const pending = useRef<{ layoutId: string; doc: LayoutDocument } | null>(null)
  // The waiting save and the one under way as state, by design, for the bar's
  // save status; plus when a save last landed.
  const [queuedFor, setQueuedFor] = useState<string | null>(null)
  const [sendingFor, setSendingFor] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const queue = useCallback((next: { layoutId: string; doc: LayoutDocument } | null) => {
    pending.current = next
    setQueuedFor(next?.layoutId ?? null)
  }, [])
  // The saves on their way to the server, one at a time (layout/saveQueue.ts).
  const sendRef = useRef<(layoutId: string, next: LayoutDocument) => Promise<boolean>>(async () => false)
  const [saves] = useState(() => saveQueue<LayoutDocument>((id, next) => sendRef.current(id, next), setSendingFor))
  // The draft revision this editor's edit stands on, and whether a save found the
  // design saved somewhere else since. Once moved, nothing more is saved: every
  // later save would be refused too, and the person has to choose the other version
  // or keep theirs knowingly (Reload).
  const revision = useRef<Revision | null>(null)
  // The design open in the editor, set the moment it is opened (current follows a render later).
  const openId = useRef<string | null>(null)
  const [moved, setMoved] = useState(false)
  // The last save was refused: what is on screen is not kept, and leaving loses it.
  const [refusedDraft, setRefusedDraft] = useState(false)
  // Where the person asked to go while the draft was not kept, waiting on a yes.
  const [leaving, setLeaving] = useState<Route | null>(null)
  const dirty = refusedDraft || moved

  const notify = useCallback((text: string) => void showToast(text), [])

  // The address. Back and forward move between the landing, a group and a design;
  // a link from elsewhere in the product (App's navigate announces a hash with a
  // hashchange) lands on the design it names.
  useEffect(() => {
    const sync = () => setRoute(routeOf(window.location.hash))
    const linked = () => {
      const to = routeOf(window.location.hash)
      fromElsewhere.current = Boolean(to.id)
      setRoute(to)
    }
    window.addEventListener('popstate', sync)
    window.addEventListener('hashchange', linked)
    return () => {
      window.removeEventListener('popstate', sync)
      window.removeEventListener('hashchange', linked)
    }
  }, [])
  const go = useCallback((to: Route) => {
    fromElsewhere.current = false
    const hash = to.id ?? to.group ?? ''
    window.history.pushState(null, '', window.location.pathname + (hash ? `#${encodeURIComponent(hash)}` : ''))
    setRoute(to)
  }, [])

  const load = useCallback(async () => {
    try {
      const listed = await api.layouts()
      setLayouts(listed.layouts)
      setLoaded(true)
      return listed.layouts
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
      return []
    }
  }, [lang])

  useEffect(() => {
    void load()
  }, [load])

  // The branches, and every machine in them: what the list counts
  // "N branches · M machines" from. One request per branch, once, when the
  // page opens: there is no route that lists an organisation's machines in one
  // answer. Inactive branches and machines are left out — they show nothing.
  useEffect(() => {
    let live = true
    api
      .branches()
      .then(async (b) => {
        const active = branchesOf(b.branches, workingIn).filter((branch) => branch.active)
        if (!live) return
        setBranches(active)
        const units = await Promise.all(
          active.map((branch) =>
            api
              .units(branch.id)
              .then((r) => r.units ?? [])
              .catch(() => null),
          ),
        )
        if (!live) return
        setMachines(units.flatMap((u) => u ?? []).filter((u) => u.active))
        setMachinesKnown(units.every((u) => u !== null))
      })
      .catch(() => setError(say('designs.page.branchesFailed')))
    return () => {
      live = false
    }
    // Once per organisation; the words of a failure follow the reader at the time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workingIn])

  // The designs to start from are a few kilobytes each and are wanted only when
  // somebody is about to start one.
  useEffect(() => {
    if (!creating || templates) return
    api
      .layoutTemplates()
      .then((t) => setTemplates(t.templates))
      .catch(() => setError(say('designs.page.templatesFailed')))
  }, [creating, templates, say])

  // The catalogue of the organisation a design belongs to: a kiosk's grid draws
  // and chooses from it, and a printed ticket is assigned to its services. Named by
  // the design's own organisation, else the one the page works in.
  const [catalogue, setCatalogue] = useState<Service[] | undefined>(undefined)
  const catalogueOf = current?.tenantId || workingIn
  useEffect(() => {
    setCatalogue(undefined)
    if (!catalogueOf) return
    let live = true
    api
      .catalogueServices(catalogueOf)
      .then((r) => {
        if (live) setCatalogue(r.services)
      })
      .catch(() => {
        if (live) setCatalogue([])
      })
    return () => {
      live = false
    }
  }, [catalogueOf])

  // The organisation's operation profiles and their documents: a kiosk design
  // offers only its profile's services. A document that failed to load is null,
  // and the full catalogue is offered instead (the server still checks).
  const [opProfiles, setOpProfiles] = useState<OperationProfileRow[]>([])
  const [opDocs, setOpDocs] = useState<ProfileDocs>(null)
  useEffect(() => {
    setOpDocs(null)
    if (!catalogueOf) return
    let live = true
    api
      .operationProfiles(catalogueOf === identity.staff.tenantId ? undefined : catalogueOf)
      .then(async ({ profiles }) => {
        if (!live) return
        setOpProfiles(profiles)
        const docs = await Promise.all(
          profiles.map((p) =>
            api
              .operationDocument(p.id)
              .then((d) => d.document)
              .catch(() => null),
          ),
        )
        if (live) setOpDocs(Object.fromEntries(profiles.map((p, i) => [p.id, docs[i] ?? null])))
      })
      .catch(() => {
        if (!live) return
        setOpProfiles([])
        setOpDocs({})
      })
    return () => {
      live = false
    }
  }, [catalogueOf, identity.staff.tenantId])

  const open = useCallback(async (layout: StoredLayout) => {
    revision.current = { layoutId: layout.id, rev: layout.draftRevision }
    openId.current = layout.id
    setMoved(false)
    setRefusedDraft(false)
    setCurrent(layout)
    setDoc(layout.draft)
    setSelectedId(null)
    setShowHistory(false)
    setShowProblems(false)
    setScreen(firstScreen(layout.kind) ?? 'attract')
    setPage(layout.draft.pages?.[0]?.id ?? MENU_PAGE)
    // "Saved" said about the design just left, read on the one just opened, is
    // not a stale word — it is a wrong one.
    setSavedAt(null)
    setError('')
    undoStack.current = []
    redoStack.current = []
    window.scrollTo(0, 0)
    try {
      const versions = await api.layoutVersions(layout.id)
      setHistory(versions.versions)
    } catch {
      setHistory([])
    }
  }, [])

  // What a refused save or publish says, in one place: both meet the same
  // refusals, because Publish saves first.
  const refusal = useCallback(
    (err: unknown, next: LayoutDocument, fallback: string) =>
      draftMoved(err)
        ? say('errors.DRAFT_MOVED')
        : refusedForSignage(err, next)
        ? say('designs.media.unlicensed')
        : filmGone(err)
          ? say('errors.VALIDATION_FAILED.FILM_GONE')
          : mediaIncomplete(err)
            ? say('errors.VALIDATION_FAILED.MEDIA_INCOMPLETE')
            : // The editor already lists, in the reader's language, what is wrong
            // with a design it knows to be unfinished; the server's own sentence
            // for the same refusal is English and says it again.
            err instanceof ApiError && err.status === 422 && problems(next).length > 0
            ? say('designs.draftNotKept')
            : err instanceof ApiError
              ? explain(err, lang, 'common.unreachable')
              : fallback,
    [say, lang],
  )

  const send = useCallback(
    async (layoutId: string, next: LayoutDocument): Promise<boolean> => {
      if (moved) return false
      // An answer about a design no longer open says nothing about the one that is.
      const open = () => openId.current === layoutId
      // Never with another design's revision; leaving a design settles its saves first.
      const base = revisionFor(revision.current, layoutId)
      if (!base) return false
      const landed = (rev: number | undefined) => {
        revision.current = afterSave(revision.current, layoutId, rev)
        if (!open()) return true
        // A refusal of an earlier save is no longer true once one lands, however
        // the change reached here (an edit, an undo, or an upload finishing).
        setError('')
        setRefusedDraft(false)
        setSavedAt(Date.now())
        return true
      }
      try {
        const saved = await api.saveDraft(layoutId, next, base.rev)
        return landed(saved.draftRevision)
      } catch (err) {
        // A save whose answer was lost landed all the same: the stored draft is this one.
        if (draftMoved(err)) {
          const stored = await api.layout(layoutId).catch(() => null)
          if (stored && sameDocument(stored.layout.draft, next)) return landed(stored.layout.draftRevision)
        }
        // Shown rather than swallowed: the person has been drawing for ten
        // minutes and needs to know none of it has been kept.
        if (!open()) return false
        if (draftMoved(err)) setMoved(true)
        setError(refusal(err, next, say('designs.page.notSaved')))
        setRefusedDraft(true)
        return false
      }
    },
    [refusal, moved, say],
  )
  sendRef.current = send

  // After any save already on its way, never beside it.
  const saveNow = useCallback(
    (layoutId: string, next: LayoutDocument): Promise<boolean> => {
      queue(null)
      return saves.save(layoutId, next)
    },
    [saves, queue],
  )

  const scheduleSave = useCallback(
    (layoutId: string, next: LayoutDocument) => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current)
      queue({ layoutId, doc: next })
      saveTimer.current = window.setTimeout(() => void saveNow(layoutId, next), SAVE_AFTER_MS)
    },
    [saveNow, queue],
  )

  // commit records a step for undo and schedules a save. Dragging calls onChange
  // dozens of times and commit once, at the end.
  const commit = useCallback(
    (next: LayoutDocument, before?: LayoutDocument) => {
      if (!current || !doc) return
      // A drag has already drawn its moves into doc; undo goes back to where it began.
      undoStack.current = [...undoStack.current.slice(-49), before ?? doc]
      redoStack.current = []
      setDoc(next)
      setError('')
      scheduleSave(current.id, next)
    },
    [current, doc, scheduleSave],
  )

  // The document and the design as they are now, for work that finishes later than
  // the render that started it: an upload into a playlist. Read through refs, not
  // through commit's closure, which holds the document as it was when the upload
  // began and would write back over everything edited while the file was on its way.
  const docRef = useRef(doc)
  docRef.current = doc
  const currentRef = useRef(current)
  currentRef.current = current

  // A change that lands when an upload finishes, made to the document as it is
  // then. layoutId is the design that was open when the upload started, bound
  // into the callbacks handed to the inspector at that render: if another design
  // is open by the time it finishes, nothing is written. The element id alone
  // cannot tell — a copied design repeats its original's element ids, so the same
  // playlist id can be found in the design opened meanwhile.
  const changeLater = useCallback(
    (layoutId: string, change: (doc: LayoutDocument) => LayoutDocument) => {
      const now = docRef.current
      const layout = currentRef.current
      if (!now || !layout || layout.id !== layoutId) return
      const next = change(now)
      // The element or its time has gone: the file is uploaded and in the list
      // to choose, and the design is left alone.
      if (next === now) return
      undoStack.current = [...undoStack.current.slice(-49), now]
      redoStack.current = []
      docRef.current = next
      setDoc(next)
      scheduleSave(layout.id, next)
    },
    [scheduleSave],
  )

  const addMediaItem = useCallback(
    (layoutId: string, elementId: string, target: MediaTarget, item: MediaItem) =>
      changeLater(layoutId, (now) => withMediaItem(now, elementId, target, item)),
    [changeLater],
  )

  const setPicture = useCallback(
    (layoutId: string, elementId: string, assetId: string) =>
      changeLater(layoutId, (now) => withPictureAsset(now, elementId, assetId)),
    [changeLater],
  )

  const undo = useCallback(() => {
    const previous = undoStack.current.pop()
    if (!previous || !current || !doc) return
    redoStack.current = [...redoStack.current, doc]
    setDoc(previous)
    scheduleSave(current.id, previous)
  }, [current, doc, scheduleSave])

  const redo = useCallback(() => {
    const next = redoStack.current.pop()
    if (!next || !current || !doc) return
    undoStack.current = [...undoStack.current, doc]
    setDoc(next)
    scheduleSave(current.id, next)
  }, [current, doc, scheduleSave])

  const picked = useMemo(() => (doc ? doc.elements.filter((el) => selection.includes(el.id)) : []), [doc, selection])
  const selected = picked.length === 1 ? picked[0]! : null

  const remove = useCallback(
    (id: string) => {
      if (!doc) return
      setSelectedId(null)
      commit({ ...doc, elements: doc.elements.filter((el) => el.id !== id) })
    },
    [doc, commit],
  )

  // Forward and back move past the next element on this screen, not past one only
  // another kiosk screen shows, so every press visibly does something. The depths
  // are written afresh in tens in the new order: two elements at the same depth
  // are drawn by identifier (drawOrder), and swapping two equal numbers would
  // change nothing.
  const restack = useCallback(
    (id: string, dir: 1 | -1) => {
      if (!doc) return
      const order = drawOrder(doc.elements)
      const i = order.findIndex((el) => el.id === id)
      let j = i + dir
      while (j >= 0 && j < order.length && !onScreen(order[j]!, drawnScreen(doc, screen))) j += dir
      if (i < 0 || j < 0 || j >= order.length) return
      const [moved] = order.splice(i, 1)
      order.splice(j, 0, moved!)
      const depth = new Map(order.map((el, n) => [el.id, (n + 1) * 10]))
      commit({ ...doc, elements: doc.elements.map((el) => ({ ...el, z: depth.get(el.id) ?? el.z })) })
    },
    [doc, screen, commit],
  )

  // The save status pressed: the waiting save goes now; a refused one is tried
  // again; with nothing to send, the draft on screen is the stored one.
  const saveNowPressed = useCallback(async () => {
    if (!current || !doc) return
    const waiting = pending.current
    if (waiting?.layoutId === current.id) {
      if (saveTimer.current) window.clearTimeout(saveTimer.current)
      await saveNow(waiting.layoutId, waiting.doc)
    } else if (saves.running()?.key === current.id) {
      return
    } else if (refusedDraft) {
      if (!moved) await saveNow(current.id, doc)
    } else {
      setSavedAt(Date.now())
    }
  }, [current, doc, refusedDraft, moved, saveNow, saves])

  // Escape lets go of the selection; the arrows move it, because a mouse cannot
  // reliably place something one pixel from where it is. Nothing else is a key.
  const overlaid = publishing || showHistory || using !== null || deleting !== null || leaving !== null
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // Not behind a dialog, a menu or the to-fix list, nor while leaving, nor in the preview.
      if (!doc || overlaid || previewing || settlingRef.current > 0 || event.defaultPrevented) return
      if (document.querySelector('[role="dialog"], [role="alertdialog"], [role="menu"], .studio-issues')) return
      if (picked.length === 0 || event.metaKey || event.ctrlKey || event.altKey) return
      const target = event.target as HTMLElement | null
      if (target?.closest('input, textarea, select, [contenteditable], [role="listbox"], [aria-haspopup]')) return
      if (event.key === 'Escape') {
        setSelection([])
        return
      }
      const step = event.shiftKey ? NUDGE_FAR : NUDGE
      const deltas: Record<string, [number, number]> = {
        ArrowLeft: [-step, 0],
        ArrowRight: [step, 0],
        ArrowUp: [0, -step],
        ArrowDown: [0, step],
      }
      const delta = deltas[event.key]
      if (!delta) return
      event.preventDefault()
      commit(moveMany(doc, picked.map((el) => el.id), delta[0], delta[1]))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [doc, picked, previewing, overlaid, commit])

  // A copy of the chosen elements beside them, chosen in their place.
  const duplicateElements = useCallback(
    (ids: readonly string[]) => {
      if (!doc) return
      const made = copies(doc, doc.elements.filter((el) => ids.includes(el.id)), 20)
      if (made.length === 0) return
      commit({ ...doc, elements: [...doc.elements, ...made] })
      setSelection(made.map((el) => el.id))
    },
    [doc, commit],
  )

  const copyPicked = useCallback(
    (ids: readonly string[]) => {
      if (!doc) return
      copyElements(doc.kind, doc.elements.filter((el) => ids.includes(el.id)))
      notify(say('designs.clip.copied'))
    },
    [doc, notify, say],
  )

  // Onto the screen and page being drawn; only what is seen there is selected.
  const pasteHere = useCallback(() => {
    if (!doc || !current) return
    const drawn = drawnScreen(doc, screen)
    const shown = pageIds(doc).includes(page) ? page : pageIds(doc)[0]
    const out = pasteClip(doc, current.id, drawn, shown)
    if (!out) return
    if (out.made.length === 0) {
      notify(say('designs.clip.refused'))
      return
    }
    commit({ ...doc, elements: [...doc.elements, ...out.made] })
    setSelection(out.made.filter((el) => onScreen(el, drawn, drawn === 'choosing' ? shown : undefined)).map((el) => el.id))
    if (out.left > 0) notify(say('designs.clip.partly'))
  }, [doc, current, screen, page, commit, notify, say])

  async function create(of: {
    kind: LayoutKind
    template?: string
    copyOf?: StoredLayout
    name: string
    /** What to save over the built-in drawing: a copy's draft, or a start redrawn
     *  for the chosen screen. */
    document?: LayoutDocument
    /** A kiosk design drawn from its profile's services (layout/kioskTemplates.ts),
     *  saved over the built-in drawing. */
    made?: LayoutDocument
    /** Kiosk designs only: the operation profile it belongs to. */
    operationProfileId?: string
  }): Promise<string | null> {
    try {
      // Not the default: that is set on Use.
      const created = await api.createLayout({
        kind: of.kind,
        template: of.template,
        name: of.name,
        default: false,
        operationProfileId: of.kind === 'kiosk' ? of.operationProfileId : undefined,
      })
      // A copy is a design started from the built-in one with the other's draft
      // saved over it at once: there is no copy on the server, and these are the
      // two calls it would make. If the second is refused, the design is there
      // with the built-in drawing and the refusal is said.
      let copyRefused = ''
      // The editor opens the built-in drawing, so "fix the problems" would point at nothing.
      const notKept = (err: unknown, d: LayoutDocument, key: StaffKey) => {
        const said = refusal(err, d, say(key))
        return said === say('designs.draftNotKept') ? say(key) : said
      }
      const over = of.document ?? of.copyOf?.draft
      if (over && !of.made) {
        try {
          await api.saveDraft(created.layout.id, over)
        } catch (err) {
          copyRefused = notKept(err, over, of.copyOf ? 'designs.page.copyNotSaved' : 'designs.page.designNotSaved')
        }
      }
      if (of.made) {
        try {
          await api.saveDraft(created.layout.id, of.made)
        } catch (err) {
          copyRefused = notKept(err, of.made, 'designs.page.designNotSaved')
        }
      }
      setCreating(false)
      const listed = await load()
      const made = listed.find((l) => l.id === created.layout.id) ?? created.layout
      await open(made)
      go({ id: made.id })
      if (copyRefused) setError(copyRefused)
      return null
    } catch (err) {
      return explain(err, lang, 'designs.page.notCreated')
    }
  }

  async function publish(note: string) {
    if (!current || !doc) return
    setError('')
    try {
      if (saveTimer.current) window.clearTimeout(saveTimer.current)
      // Saved first, deliberately: publishing what is on screen rather than what
      // happened to have been saved is the only behaviour anybody expects. Through
      // the queue, after any save already on its way; a refusal is said by send.
      if (moved) throw new ApiError(409, 'DRAFT_MOVED', '')
      if (!(await saveNow(current.id, doc))) {
        setPublishing(false)
        return
      }
      const { version } = await api.publishLayout(current.id, note || undefined)
      // What the screens are showing is now what is in the editor. Recorded here
      // rather than waiting for the list to come back, because it is what Publish
      // reads to decide whether it has anything left to do: without it the button
      // stays live after a successful publish, inviting a second one.
      const published = doc
      setCurrent((c) =>
        c && c.id === current.id ? { ...c, published, version, publishedAt: new Date().toISOString() } : c,
      )
      setPublishing(false)
      notify(say('designs.page.versionPublished', { version }))
      const versions = await api.layoutVersions(current.id)
      setHistory(versions.versions)
      await load()
    } catch (err) {
      setPublishing(false)
      if (draftMoved(err)) setMoved(true)
      setError(refusal(err, doc, say('designs.page.notPublished')))
    }
  }

  // Use's Apply: the design's draft goes live first. The open design is kept first
  // (what is on screen is what goes out); another is published as stored.
  async function publishFor(layout: StoredLayout): Promise<string | null> {
    const open = current?.id === layout.id ? doc : null
    try {
      if (open) {
        if (saveTimer.current) window.clearTimeout(saveTimer.current)
        if (!(await keepDraft(layout.id))) return say('designs.page.notSaved')
      }
      const { version } = await api.publishLayout(layout.id)
      if (open) {
        setCurrent((c) => (c && c.id === layout.id ? { ...c, published: open, version, publishedAt: new Date().toISOString() } : c))
        void api.layoutVersions(layout.id).then((v) => setHistory(v.versions)).catch(() => {})
      }
      await load()
      return null
    } catch (err) {
      return refusal(err, open ?? layout.draft, say('designs.page.notPublished'))
    }
  }

  // The other version, opened afresh: what was drawn here since is dropped, which
  // is what the person chose by pressing Reload rather than copying it first.
  async function reloadMoved() {
    if (!current) return
    try {
      const { layout } = await api.layout(current.id)
      await open(layout)
    } catch (err) {
      setError(explain(err, lang, 'designs.page.notLoaded'))
    }
  }

  async function restore(version: LayoutVersion) {
    if (!current) return
    try {
      // What waits is dropped (the restored version replaces it); what is under way lands first.
      if (saveTimer.current) window.clearTimeout(saveTimer.current)
      queue(null)
      await saves.running()?.done
      const { layout, draftRevision } = await api.restoreLayout(current.id, version.version)
      revision.current = { layoutId: current.id, rev: draftRevision }
      setMoved(false)
      setRefusedDraft(false)
      setError('')
      setSavedAt(Date.now())
      undoStack.current = doc ? [...undoStack.current, doc] : undoStack.current
      setDoc(layout)
      setSelectedId(null)
      setShowHistory(false)
      notify(say('designs.page.versionRestored', { version: version.version }))
    } catch (err) {
      setError(explain(err, lang, 'designs.page.notRestored'))
    }
  }

  /** Makes a design the default (or not) and, for a slip, sets its services. */
  async function saveUse(layout: StoredLayout, a: UseChoice, close = true): Promise<string | null> {
    try {
      const { layout: saved } = await api.setLayoutUse(layout.id, {
        default: a.isDefault,
        ...(layout.kind === 'ticket' ? { serviceIds: [...a.serviceIds] } : {}),
      })
      setCurrent((c) => (c && c.id === saved.id ? { ...c, ...usesOf(saved) } : c))
      if (close) {
        setUsing(null)
        notify(say('designs.page.saved'))
      }
      await load()
      return null
    } catch (err) {
      return explain(err, lang, 'designs.page.notSaved')
    }
  }

  /** A new unassigned draft of the design's latest document, opened in the editor. */
  async function duplicate(layout: StoredLayout) {
    if (duplicating) return
    setDuplicating(true)
    try {
      // The server copies the stored draft. A save already on its way is waited
      // for, and an edit still waiting for its timer goes now; one that is not
      // stored stops the copy, and its refusal stays said.
      const sending = saves.running()
      if (sending?.key === layout.id && !(await sending.done)) return
      if (current?.id === layout.id) {
        // A draft the server has not kept is not what would be copied: stop, and say why.
        if (moved) {
          setError(say('errors.DRAFT_MOVED'))
          return
        }
        const waiting = pending.current
        if (waiting?.layoutId === layout.id) {
          if (saveTimer.current) window.clearTimeout(saveTimer.current)
          if (!(await saveNow(waiting.layoutId, waiting.doc))) return
        } else if (refusedDraft) {
          // Tried again, so a refusal that has passed (the network) does not block the copy.
          const now = docRef.current
          if (!now || !(await saveNow(layout.id, now))) return
        }
      }
      setError('')
      const made = await api.duplicateLayout(layout.id, copyNameWithin(copyBase(nameOf(layout)), (name) => say('designs.page.copyName', { name })))
      await load()
      await open(made.layout)
      go({ id: made.layout.id })
    } catch (err) {
      setError(explain(err, lang, 'designs.page.copyNotMade'))
    } finally {
      setDuplicating(false)
    }
  }

  /** Renames a design; the answer is the refusal to show, or null. */
  async function rename(layout: StoredLayout, name: string): Promise<string | null> {
    const wrong = nameProblem(name, layout.kind, layouts, layout.id)
    if (wrong) return say(NAME_PROBLEM[wrong])
    if (name.trim() === layout.name) return null
    try {
      const { layout: renamed } = await api.renameLayout(layout.id, name.trim())
      const named = (l: StoredLayout) => (l.id === layout.id ? { ...l, name: renamed.name } : l)
      setLayouts((ls) => ls.map(named))
      setCurrent((c) => c && named(c))
      return null
    } catch (err) {
      return explain(err, lang, 'designs.page.notRenamed')
    }
  }

  async function deleteDesign(layout: StoredLayout): Promise<string | null> {
    try {
      await api.deleteLayout(layout.id)
      setDeleting(null)
      if (current?.id === layout.id) {
        if (saveTimer.current) window.clearTimeout(saveTimer.current)
        queue(null)
        openId.current = null
        setCurrent(null)
        setDoc(null)
        go({ group: layout.kind })
      }
      await load()
      notify(say('designs.page.deleted'))
      return null
    } catch (err) {
      return explain(err, lang, 'designs.page.notDeleted')
    }
  }

  const closeDesign = useCallback(async () => {
    // A save still waiting for its timer goes now, so the gallery draws it.
    const waiting = pending.current
    if (saveTimer.current) window.clearTimeout(saveTimer.current)
    openId.current = null
    setCurrent(null)
    setDoc(null)
    setSelectedId(null)
    setShowHistory(false)
    setPublishing(false)
    setUsing(null)
    setDeleting(null)
    setSavedAt(null)
    if (waiting) await saveNow(waiting.layoutId, waiting.doc)
    await load()
  }, [saveNow, load])

  // The address decides which design is open. Opening one by a click writes its
  // address first and lands here like a link or a reload does.
  //
  // asking is the design last asked for, so an answer that comes back after the
  // person has already clicked another one is dropped rather than opened over it.
  const asking = useRef<string | undefined>(undefined)
  // The design whose answer is on its way.
  const fetching = useRef<string | null>(null)
  const dirtyRef = useRef(dirty)
  dirtyRef.current = dirty
  const movedRef = useRef(moved)
  movedRef.current = moved
  const refusedRef = useRef(refusedDraft)
  refusedRef.current = refusedDraft

  // Closing or reloading the tab: a save waiting for its timer goes now; the browser
  // asks only while a save is on its way or the draft is not kept.
  useEffect(() => {
    if (!current) return
    const onUnload = (e: BeforeUnloadEvent) => {
      if (dirtyRef.current || saves.running()) {
        e.preventDefault()
        e.returnValue = ''
        return
      }
      const waiting = pending.current
      const base = waiting && revisionFor(revision.current, waiting.layoutId)
      if (!waiting || !base) return
      if (saveTimer.current) window.clearTimeout(saveTimer.current)
      queue(null)
      void api.saveDraft(waiting.layoutId, waiting.doc, base.rev, { keepalive: true }).catch(() => {})
    }
    window.addEventListener('beforeunload', onUnload)
    return () => window.removeEventListener('beforeunload', onUnload)
  }, [current, queue, saves])

  // A design's saves, finished before it is left: the one on its way is waited for
  // and the one waiting for its timer goes now. False when either is refused.
  // Bounded: a save that never answers counts as not kept, and the person is asked.
  const settle = useCallback(
    async (layoutId: string): Promise<boolean> => {
      const sending = saves.running()
      if (sending?.key === layoutId && !(await answeredWithin(sending.done, SETTLE_MS))) return false
      const waiting = pending.current
      if (waiting?.layoutId === layoutId) {
        if (saveTimer.current) window.clearTimeout(saveTimer.current)
        return answeredWithin(saveNow(layoutId, waiting.doc), SETTLE_MS)
      }
      return true
    },
    [saveNow],
  )
  // Everything drawn, kept before the design is left: the waiting save goes, a refused
  // one is tried again, and a draft saved elsewhere meanwhile is saved over (the last
  // to leave wins). False only when the server cannot be reached.
  const keepDraft = useCallback(
    async (layoutId: string): Promise<boolean> => {
      const settled = await settle(layoutId)
      const now = docRef.current
      if (openId.current !== layoutId || !now) return settled
      if (movedRef.current) {
        try {
          const saved = await api.saveDraft(layoutId, now)
          revision.current = { layoutId, rev: saved.draftRevision }
          setMoved(false)
          setRefusedDraft(false)
          setError('')
          return true
        } catch {
          return false
        }
      }
      if (settled && !refusedRef.current) return true
      return answeredWithin(saveNow(layoutId, now), SETTLE_MS)
    },
    [settle, saveNow],
  )
  // While a design is being left, it takes no more edits: one made then would be
  // saved after the next design had opened, and lost.
  const [settling, setSettling] = useState(false)
  // Counted, so a second leave started meanwhile keeps it frozen until both end.
  const settlingRef = useRef(0)
  const freeze = (on: boolean) => {
    settlingRef.current = Math.max(0, settlingRef.current + (on ? 1 : -1))
    setSettling(settlingRef.current > 0)
  }

  // The editor's Back: the draft is kept first; only an unreachable server keeps the person here to choose.
  const leavingNow = useRef(false)
  const leave = async (to: Route, back = false) => {
    if (!current || leavingNow.current) return
    const id = current.id
    leavingNow.current = true
    freeze(true)
    try {
      if (!(await keepDraft(id))) {
        if (openId.current === id) setLeaving(to)
        return
      }
    } finally {
      leavingNow.current = false
      freeze(false)
    }
    if (openId.current !== id) return
    if (back && fromElsewhere.current && window.history.length > 1) window.history.back()
    else go(to)
  }
  useEffect(() => {
    asking.current = route.id
    // Back to the design being left, and the question where the person was going.
    const stay = () => {
      const id = openId.current
      if (!id) return
      window.history.pushState(null, '', window.location.pathname + `#${encodeURIComponent(id)}`)
      setLeaving(route)
      setRoute({ id })
    }
    if (route.id) {
      // The ref, not the render's current: a design closed or opened since this render counts.
      if (openId.current === route.id || fetching.current === route.id || !loaded) return
      // Asked for afresh rather than taken from the list. The list is as old as the
      // tab: a design changed elsewhere since would open with the old drawing (and
      // its first save be refused as moved), and one made elsewhere since — a link
      // a colleague sent — would not be in it at all.
      const asked = route.id
      const from = openId.current ?? undefined
      // The design being left, kept; an unreachable server stays and asks. One no
      // longer open has nothing to stay on, so the asked design opens anyway.
      const kept = async () => {
        if (!from || (await keepDraft(from)) || openId.current !== from) return true
        if (asking.current === asked) stay()
        return false
      }
      fetching.current = asked
      if (from) freeze(true)
      void (async () => {
        if (!(await kept())) return
        return api.layout(asked)
      })()
        .then(async (answer) => {
          if (!answer) return
          const { layout } = answer
          if (asking.current !== asked) return
          if (!layouts.some((l) => l.id === asked)) await load()
          // Again, just before the switch: an upload that finished meanwhile saves too.
          if (!(await kept()) || asking.current !== asked) return
          // Not awaited: the next design takes edits as soon as it is drawn.
          void open(layout)
        })
        .catch((err) => {
          if (asking.current !== asked) return
          const found = layouts.find((l) => l.id === asked)
          if (found && !(err instanceof ApiError && err.status === 404)) {
            void open(found)
            return
          }
          setError(say('designs.page.noSuchDesign'))
          go({})
        })
        .finally(() => {
          if (fetching.current === asked) fetching.current = null
          if (from) freeze(false)
        })
    } else if (openId.current) {
      const from = openId.current
      freeze(true)
      void keepDraft(from)
        .then((kept) => {
          if (asking.current !== undefined || openId.current !== from) return
          if (kept) void closeDesign()
          else stay()
        })
        .finally(() => freeze(false))
    }
    // Only the address and the list's arrival decide this; current is read, not
    // watched, so saving a draft does not reopen the design. Every go() is a new
    // route, so a click on a design that is not open asks for it again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route, loaded])

  // While a screen design is open, its machines are asked again every ten seconds,
  // so "2 of 3 showing it" moves to 3 of 3 as they draw a publish. Only the
  // branches its machines stand in; a ticket is paper and reports nothing.
  const watchedBranches = current && current.kind !== 'ticket'
    ? [...new Set(shownOn(current, layouts, machines, branches, catalogue).machines.map((m) => m.branchId))].sort().join(',')
    : ''
  const refreshMachines = useCallback(async (ids: readonly string[]) => {
    if (ids.length === 0) return
    const fresh = await Promise.all(ids.map((id) => api.units(id).then((r) => r.units ?? []).catch(() => null)))
    setMachines((all) => withBranchMachines(all, ids, fresh))
  }, [])
  useEffect(() => {
    if (!watchedBranches) return
    const ids = watchedBranches.split(',')
    const timer = window.setInterval(() => void refreshMachines(ids), 10_000)
    return () => window.clearInterval(timer)
  }, [watchedBranches, refreshMachines])

  const branchName = identity.organisation ?? ''
  const usage = useCallback(
    (layout: StoredLayout) => ({
      ...shownOn(layout, layouts, machines, branches, catalogue),
      known: machinesKnown && (layout.kind !== 'ticket' || catalogue !== undefined),
    }),
    [layouts, machines, machinesKnown, branches, catalogue],
  )
  // The open design as the list knows it, so a fresh default is counted.
  const listedCurrent = current ? { ...current, ...usesOf(layouts.find((l) => l.id === current.id) ?? current) } : null
  const group = route.group ?? (current ? current.kind : undefined)
  // As the list knows it now, so a fresh default is counted.
  const useOf = using
    ? using.id === listedCurrent?.id
      ? listedCurrent
      : layouts.find((l) => l.id === using.id) ?? using
    : null
  // What Apply would put live: the editor's document for the open design, else the stored draft.
  const useDraft = useOf ? (useOf.id === current?.id && doc ? doc : useOf.draft ?? null) : null
  const profileName = (l: StoredLayout) => (l.kind === 'kiosk' ? opProfiles.find((p) => p.id === l.operationProfileId)?.name ?? '' : '')
  // What a design may offer: a kiosk its profile's services (undefined while
  // loading), anything else the catalogue.
  const offerOf = (l: StoredLayout): Service[] | undefined => {
    if (l.kind !== 'kiosk' || !catalogue) return catalogue
    if (!opDocs) return undefined
    const d = l.operationProfileId ? opDocs[l.operationProfileId] : null
    return d ? servicesOfProfile(catalogue, d) : catalogue
  }
  const notInProfile = (l: StoredLayout): boolean => {
    const d = l.kind === 'kiosk' && l.operationProfileId && catalogue ? opDocs?.[l.operationProfileId] : null
    return !!d && !matches(matchBranch(l.draft, servicesOfProfile(catalogue!, d)))
  }

  return (
    <Chrome title={say('designs.page.title')} setup>
      {doc && current && listedCurrent ? (
        <EditorView
          key={current.id}
          frozen={settling}
          current={listedCurrent}
          moved={moved}
          onReload={reloadMoved}
          doc={doc}
          selected={selected}
          picked={picked}
          onPick={setSelection}
          previewing={previewing}
          onPreviewing={setPreviewing}
          screen={screen}
          page={page}
          branches={current.kind === 'kiosk' ? profileBranches(branches, current.operationProfileId ?? '') : branches}
          profileName={profileName(current)}
          initialBranch={branchFilter || undefined}
          homeBranches={identity.staff.branchIds}
          language={language}
          save={saveStatus({ pending: queuedFor === current.id, inFlight: sendingFor === current.id, refused: refusedDraft, moved, savedAt })}
          error={error}
          showProblems={showProblems}
          usage={usage(listedCurrent)}
          links={
            identity.permissions.includes('branch.manage') && linksOffered(current.kind)
              ? linkRows(usage(listedCurrent).machines, branches)
              : null
          }
          onNotify={notify}
          modules={identity.modules}
          branchName={branchName}
          catalogue={offerOf(current)}
          canUndo={undoStack.current.length > 0}
          canRedo={redoStack.current.length > 0}
          onBack={() => void leave({ group: current.kind }, true)}
          onSave={() => void (moved ? keepDraft(current.id) : saveNowPressed())}
          onUndo={undo}
          onRedo={redo}
          onScreen={(s, p) => {
            setScreen(s)
            if (p) setPage(p)
            setSelectedId(null)
          }}
          onLanguage={setLanguage}
          onShowProblems={setShowProblems}
          onHistory={() => setShowHistory(true)}
          onPublish={() => setPublishing(true)}
          onUse={() => setUsing(current)}
          onDelete={() => setDeleting(current)}
          onDuplicate={() => void duplicate(current)}
          onRename={(name) => rename(current, name)}
          duplicating={duplicating}
          onSelect={setSelectedId}
          onChange={setDoc}
          onCommit={commit}
          onRestack={restack}
          onRemove={remove}
          onDuplicateElements={duplicateElements}
          onCopyElements={copyPicked}
          onPaste={pasteHere}
          onAddMediaItem={(elementId, target, item) => addMediaItem(current.id, elementId, target, item)}
          onSetPicture={(elementId, assetId) => setPicture(current.id, elementId, assetId)}
        />
      ) : route.id ? (
        <main className="studio-page">
          <p className="studio-muted">{say('common.loading')}</p>
        </main>
      ) : group ? (
        <GroupView
          kind={group}
          branches={branches}
          branchFilter={branchFilter}
          onBranchFilter={setBranchFilter}
          layouts={layouts.filter((l) => l.kind === group)}
          loaded={loaded}
          error={error}
          branchName={branchName}
          services={catalogue}
          usage={usage}
          profileName={profileName}
          notInProfile={notInProfile}
          onBack={() => go({})}
          onOpen={(l) => go({ id: l.id })}
          onDuplicate={(l) => void duplicate(l)}
          onUse={setUsing}
          onDelete={setDeleting}
          onRename={setRenaming}
          duplicating={duplicating}
          onNew={() => setCreating(true)}
        />
      ) : (
        <Landing
          layouts={layouts}
          loaded={loaded}
          error={error}
          branchName={branchName}
          services={catalogue}
          onGroup={(k) => go({ group: k })}
          onNew={() => setCreating(true)}
        />
      )}

      {creating && (
        <NewDesign
          initialKind={group ?? 'display'}
          layouts={layouts}
          templates={templates}
          branchName={branchName}
          services={catalogue}
          onClose={() => setCreating(false)}
          profiles={opProfiles}
          profileDocs={opDocs}
          onCreate={create}
        />
      )}

      {useOf && (
        <UseDrawer
          layout={useOf}
          layouts={layouts}
          profileName={profileName(useOf)}
          services={catalogue}
          onClose={() => setUsing(null)}
          needsPublish={useDraft !== null && (!useOf.published || !sameDocument(useDraft, useOf.published))}
          faulty={useDraft !== null && problems(useDraft, lang).length > 0}
          onPublish={() => publishFor(useOf)}
          onSave={(a, close) => saveUse(useOf, a, close)}
          onApplied={(ids) => {
            void load()
            void refreshMachines(ids)
          }}
          onDone={() => {
            setUsing(null)
            notify(say('designs.use.outcome.applied'))
          }}
        />
      )}

      {leaving && (
        <LeaveDialog
          onStay={() => setLeaving(null)}
          onLeave={() => {
            const to = leaving
            setLeaving(null)
            // What the server refused is dropped, not sent again from the list.
            if (saveTimer.current) window.clearTimeout(saveTimer.current)
            queue(null)
            saves.drop()
            // The refusal belonged to the draft just dropped, not to the list.
            setError('')
            setSavedAt(null)
            setRefusedDraft(false)
            setMoved(false)
            dirtyRef.current = false
            go(to)
          }}
        />
      )}

      {renaming && (
        <RenameDialog
          name={nameOf(renaming)}
          onClose={() => setRenaming(null)}
          onRename={async (name) => {
            const answer = await rename(renaming, name)
            if (!answer) setRenaming(null)
            return answer
          }}
        />
      )}

      {deleting && (
        <DeleteDialog name={nameOf(deleting)} onClose={() => setDeleting(null)} onDelete={() => deleteDesign(deleting)} />
      )}

      {publishing && listedCurrent && doc && (
        <PublishDialog
          name={nameOf(listedCurrent)}
          kind={listedCurrent.kind}
          faults={problems(doc, lang)}
          usage={usage(listedCurrent)}
          branches={branches}
          onClose={() => setPublishing(false)}
          onPublish={(note) => void publish(note)}
        />
      )}

      {showHistory && current && doc && (
        <HistoryDrawer
          kind={current.kind}
          doc={doc}
          history={history}
          branchName={branchName}
          services={catalogue}
          onClose={() => setShowHistory(false)}
          onRestore={(v) => void restore(v)}
        />
      )}
    </Chrome>
  )
}

/** The choosing half of a design as the list reports it. */
function usesOf(l: StoredLayout): Pick<StoredLayout, 'isDefault' | 'serviceIds'> {
  return { isDefault: l.isDefault, serviceIds: l.serviceIds }
}

/** A save refused because the design was saved somewhere else since it was opened. */
function draftMoved(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.code === 'DRAFT_MOVED'
}

// ---------------------------------------------------------------- who uses a design

/** "K1 Gəncə offline", "K2 Nizami on version 15", "K3 Gəncə old app" — a few
 *  words a person can act on. The branch is named because every branch's first
 *  kiosk is K1. */
function behindWords(b: Behind, branches: readonly Branch[], say: Say): string {
  const where = branches.find((x) => x.id === b.unit.branchId)?.name
  const who = where ? `${b.unit.code} ${where}` : b.unit.code
  switch (b.why) {
    case 'offline':
      return say('designs.page.offline', { who })
    case 'version':
      return b.version !== undefined ? say('designs.page.onVersion', { who, version: b.version }) : say('designs.page.onOther', { who })
    case 'app':
      return say('designs.page.oldApp', { who })
  }
}

/** "· 2 of 3 showing it · K1 on version 15": what the machines report having drawn. */
function ReachLine({ layoutId, version, machines, branches }: { layoutId: string; version: number; machines: readonly Unit[]; branches: readonly Branch[] }) {
  const { say } = useStaffText()
  const r = reach(layoutId, version, machines)
  const named = r.behind.slice(0, 3).map((b) => behindWords(b, branches, say))
  if (r.behind.length > 3) named.push(say('designs.page.more', { n: r.behind.length - 3 }))
  const line = say('designs.page.showing', { showing: r.showing, of: r.of }) + (named.length > 0 ? ` · ${named.join(', ')}` : '')
  return (
    <>
      <span aria-hidden="true"> · </span>
      <span className="studio-reach" data-behind={r.behind.length > 0 || undefined} title={line}>
        {line}
      </span>
    </>
  )
}

/** Who uses a design. known is false while the machines (or, for a ticket, the
 *  services) have not all loaded: then nothing is claimed, rather than "not used"
 *  about a design on every board in the branch. */
type Usage = {
  /** Branches of the machines that use it. */
  branches: Branch[]
  /** Machines that show or print it, by their own choice or the default. */
  machines: Unit[]
  /** Machines whose settings name it. */
  ownMachines: Unit[]
  services: Service[]
  known?: boolean
}

/**
 * Who uses a design, worked out the way the server answers a live machine
 * (lib/branchDesigns' designFor): its choice, else the default that applies to it
 * (a kiosk: its branch's operation profile's). Publishing is not considered —
 * this is who chose it; the server skips a choice with nothing published.
 */
export function shownOn(
  layout: StoredLayout,
  all: readonly StoredLayout[],
  machines: readonly Unit[],
  branches: readonly Branch[],
  services: readonly Service[] | undefined,
): Usage {
  const mine = all.some((l) => l.id === layout.id)
    ? all.filter((l) => l.kind === layout.kind)
    : [...all.filter((l) => l.kind === layout.kind), layout]
  const ticket = layout.kind === 'ticket'
  const profileOf = new Map(branches.map((b) => [b.id, b.operationProfileId]))
  const drawsIt = (m: Unit) => layoutKindFor(m.kind as ProfileUnitKind, ticket) === layout.kind
  const named = (m: Unit) => (ticket ? m.ticketLayoutId : m.layoutId) === layout.id
  const using = machines.filter(
    (m) =>
      designFor(m.kind as ProfileUnitKind, { layoutId: m.layoutId, ticketLayoutId: m.ticketLayoutId }, 'own', mine,
        profileOf.get(m.branchId), ticket).layout?.id === layout.id,
  )
  const at = new Set(using.map((m) => m.branchId))
  return {
    branches: branches.filter((b) => at.has(b.id)),
    machines: using,
    ownMachines: machines.filter((m) => drawsIt(m) && named(m)),
    services: ticket ? (services ?? []).filter((s) => s.active && (layout.serviceIds ?? []).includes(s.id)) : [],
  }
}

/**
 * The machine list with the given branches' machines replaced by a fresh read.
 * A branch whose read failed (null) keeps what was known.
 */
export function withBranchMachines(all: readonly Unit[], ids: readonly string[], fresh: readonly (Unit[] | null)[]): Unit[] {
  const read = new Set(ids.filter((_, i) => fresh[i] !== null))
  return [...all.filter((m) => !read.has(m.branchId)), ...fresh.flatMap((u) => u ?? []).filter((u) => u.active)]
}

/** "3 branches · 7 machines", and for a slip "· 2 services". */
export function usageLine(u: Usage, kind: LayoutKind, say: Say): string {
  if (u.known === false) return ''
  const parts = [say('designs.page.branches', { n: u.branches.length }), say('designs.page.machines', { n: u.machines.length })]
  if (kind === 'ticket') parts.push(say('designs.page.services', { n: u.services.length }))
  if (u.branches.length + u.machines.length + u.services.length === 0) return say('designs.page.notUsed')
  return parts.join(' · ')
}

// ---------------------------------------------------------------- landing and groups

/**
 * The three groups, each with how many designs it holds and its default drawn.
 * An organisation may have a hundred designs; nobody wants them on one page.
 */
function Landing({
  layouts,
  loaded,
  error,
  branchName,
  services,
  onGroup,
  onNew,
}: {
  layouts: StoredLayout[]
  loaded: boolean
  error: string
  branchName: string
  services?: Service[]
  onGroup: (k: LayoutKind) => void
  onNew: () => void
}) {
  const { say } = useStaffText()
  const [hovered, setHovered] = useState<LayoutKind | null>(null)
  return (
    <main className="studio-page">
      <header className="studio-head">
        <h1 className="studio-title">{say('designs.page.title')}</h1>
        <Button variant="primary" size="sm" onPress={onNew}>
          <Icon name="plus" size={16} /> {say('designs.page.new')}
        </Button>
      </header>
      {error && <Notice>{error}</Notice>}
      {loaded && layouts.length === 0 ? (
        <Card className="studio-card">
          <EmptyState className="studio-empty">
            <Icon name="designs" size={20} />
            <p>{say('designs.page.none')}</p>
          </EmptyState>
        </Card>
      ) : (
      <ul className="studio-groups">
        {KINDS.map((k) => {
          const mine = layouts.filter((l) => l.kind === k)
          const face = mine.find((l) => l.isDefault) ?? mine[0]
          const shown = face ? (face.published ?? face.draft) : null
          return (
            <li key={k}>
              {/* A card acting as a button: the kiosk drawn inside has buttons of
                  its own, and a button may not hold another. */}
              <Card
                role="button"
                tabIndex={0}
                className="studio-groupcard"
                onMouseEnter={() => setHovered(k)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onGroup(k)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onGroup(k)
                  }
                }}
              >
                <span className="studio-groupcard__stage" data-kind={k}>
                  {loaded && !shown && <span className="studio-groupcard__none">{say('designs.page.none')}</span>}
                  {shown && (
                    <DesignThumb kind={k} document={shown} bindings={previewBindings(shown, branchName, services, api.assetURL)} playing={hovered === k} />
                  )}
                </span>
                <Card.Footer className="studio-groupcard__body">
                  <span className="studio-groupcard__text">
                    <Card.Title className="studio-groupcard__name" title={say(KIND[k].hint)}>
                      {say(KIND[k].one)}
                    </Card.Title>
                  </span>
                  <Chip size="sm" variant="soft" className="studio-count">
                    {loaded ? mine.length : '…'}
                  </Chip>
                </Card.Footer>
              </Card>
            </li>
          )
        })}
      </ul>
      )}
    </main>
  )
}

/**
 * One group's designs: searchable, the default first, drawn with the renderer the
 * machines use. Each tile is kept out of layout work while it is off screen
 * (content-visibility in studio.css), so a hundred of them stay quick.
 */
function GroupView({
  kind,
  branches,
  branchFilter,
  onBranchFilter,
  layouts,
  loaded,
  error,
  branchName,
  services,
  usage,
  profileName,
  notInProfile,
  onBack,
  onOpen,
  onDuplicate,
  onUse,
  onDelete,
  onRename,
  duplicating,
  onNew,
}: {
  kind: LayoutKind
  branches: Branch[]
  branchFilter: string
  onBranchFilter: (id: string) => void
  layouts: StoredLayout[]
  loaded: boolean
  error: string
  branchName: string
  services?: Service[]
  usage: (l: StoredLayout) => Usage
  profileName: (l: StoredLayout) => string
  notInProfile: (l: StoredLayout) => boolean
  onBack: () => void
  onOpen: (l: StoredLayout) => void
  onDuplicate: (l: StoredLayout) => void
  onUse: (l: StoredLayout) => void
  onDelete: (l: StoredLayout) => void
  onRename: (l: StoredLayout) => void
  duplicating: boolean
  onNew: () => void
}) {
  const { say } = useStaffText()
  const [query, setQuery] = useState('')
  const q = query.trim().toLowerCase()
  // A branch's designs: what it chose, the default it follows, and what its
  // machines chose for themselves.
  const usedAt = (l: StoredLayout, branchId: string) => {
    const u = usage(l)
    return u.branches.some((b) => b.id === branchId) || u.ownMachines.some((m) => m.branchId === branchId)
  }
  const list = layouts
    .filter((l) => !q || nameOf(l).toLowerCase().includes(q))
    .filter((l) => !branchFilter || usedAt(l, branchFilter))
    .sort((a, b) => Number(!!b.isDefault) - Number(!!a.isDefault) || nameOf(a).localeCompare(nameOf(b)))
  const paged = usePaged(list, `${q}|${branchFilter}`)
  return (
    <main className="studio-page">
      <Button variant="ghost" size="sm" className="studio-crumb" onPress={onBack}>
        <span className="studio-flip">
          <Icon name="chevron" size={16} />
        </span>
        {say('designs.page.title')}
      </Button>
      <header className="studio-head">
        <h1 className="studio-title">
          {say(KIND[kind].one)}
          <Chip size="sm" variant="soft" className="studio-count">
            {layouts.length}
          </Chip>
        </h1>
        <div className="studio-head__tools">
          {kind !== 'ticket' && branches.length > 1 && (
            <div className="studio-branchfilter">
              <StudioSelect
                aria-label={say('designs.page.branch')}
                value={branchFilter}
                onChange={onBranchFilter}
                options={[{ value: '', label: say('designs.page.allBranches') }, ...branches.map((b) => ({ value: b.id, label: b.name }))]}
              />
            </div>
          )}
          {layouts.length > 6 && (
            <SearchField className="studio-search" aria-label={say('designs.page.searchDesigns')} value={query} onChange={setQuery} variant="secondary">
              <SearchField.Group>
                <SearchField.SearchIcon />
                <SearchField.Input placeholder={say('designs.page.search')} />
                <SearchField.ClearButton />
              </SearchField.Group>
            </SearchField>
          )}
          <Button variant="primary" size="sm" onPress={onNew}>
            <Icon name="plus" size={16} /> {say('designs.page.new')}
          </Button>
        </div>
      </header>

      {error && <Notice>{error}</Notice>}

      {list.length === 0 ? (
        !loaded ? (
          <p className="studio-muted">{say('common.loading')}</p>
        ) : (
          <Card className="studio-card">
            <EmptyState className="studio-empty">
              <Icon name={q ? 'search' : 'designs'} size={20} />
              <p>{q || branchFilter ? say('designs.page.nothingMatches') : say('designs.page.none')}</p>
            </EmptyState>
          </Card>
        )
      ) : (
        <>
        <ul className="studio-gallery" data-kind={kind}>
          {paged.rows.map((layout) => (
            <li key={layout.id}>
              <Tile
                layout={layout}
                usage={usage(layout)}
                branchName={branchName}
                services={services}
                profile={profileName(layout)}
                notInProfile={notInProfile(layout)}
                onOpen={() => onOpen(layout)}
              />
              {/* Beside the tile, not in it: the tile is itself a button. */}
              <div className="studio-tile__more">
                <MoreMenu
                  up
                  items={[
                    { label: say('designs.page.duplicate'), icon: 'copy', onClick: () => onDuplicate(layout), disabled: duplicating },
                    { label: say('designs.page.rename'), icon: 'edit', onClick: () => onRename(layout) },
                    { label: say('eq.design.use'), icon: 'link', onClick: () => onUse(layout) },
                    { label: say('common.delete'), icon: 'trash', onClick: () => onDelete(layout), danger: true },
                  ]}
                />
              </div>
            </li>
          ))}
        </ul>
        <Pager {...paged.pager} free />
        </>
      )}
    </main>
  )
}

function Tile({
  layout,
  usage,
  branchName,
  services,
  profile,
  notInProfile,
  onOpen,
}: {
  layout: StoredLayout
  usage: Usage
  branchName: string
  services?: Service[]
  /** A kiosk design's operation profile. */
  profile?: string
  /** A kiosk design whose buttons differ from its profile's services. */
  notInProfile?: boolean
  onOpen: () => void
}) {
  // What is on the wall if anything is, and the draft otherwise. A design that has
  // never been published shows what it would look like, which is what its author
  // is about to decide about.
  const { say, lang } = useStaffText()
  const shown = layout.published ?? layout.draft
  const [hover, setHover] = useState(false)
  const faults = problems(layout.draft).length
  const edited = !!layout.published && !sameDocument(layout.draft, layout.published)
  return (
    // A card that acts as a button, not a button: the kiosk preview inside draws
    // its service buttons as buttons. The preview is inert, so nothing in it takes
    // focus or a click of its own.
    <Card
      role="button"
      tabIndex={0}
      className="studio-tile"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      }}
    >
      <span className="studio-tile__stage" data-kind={layout.kind}>
        <DesignThumb kind={layout.kind} document={shown} bindings={previewBindings(shown, branchName, services, api.assetURL)} playing={hover} />
      </span>
      <span className="studio-tile__body">
        <span className="studio-tile__name">
          {nameOf(layout)}
          {layout.isDefault && (
            <Chip size="sm" variant="soft" color="accent" className="studio-tag">
              {say('designs.page.default')}
            </Chip>
          )}
        </span>
        <span className="studio-tile__on">
          {profile && (
            <Chip size="sm" variant="soft">
              {profile}
            </Chip>
          )}
          {usage.known === false ? (
            ''
          ) : usage.branches.length === 0 && usage.ownMachines.length === 0 && usage.services.length === 0 ? (
            say('designs.page.notUsed')
          ) : (
            <>
              {usage.branches.slice(0, SHOWN_BRANCHES).map((b) => (
                <Chip key={b.id} size="sm" variant="soft">
                  {b.name}
                </Chip>
              ))}
              {usage.branches.length > SHOWN_BRANCHES && (
                <span className="studio-chip--quiet" title={usage.branches.map((b) => b.name).join(', ')}>
                  {say('designs.page.moreBranches', { n: usage.branches.length - SHOWN_BRANCHES })}
                </span>
              )}
              {usage.ownMachines.length > 0 && (
                <span className="studio-chip--quiet">+{say('designs.page.machines', { n: usage.ownMachines.length })}</span>
              )}
              {layout.kind === 'ticket' && usage.services.length > 0 && (
                <span className="studio-chip--quiet">{say('designs.page.services', { n: usage.services.length })}</span>
              )}
            </>
          )}
        </span>
        {notInProfile && (
          <Chip size="sm" variant="soft" color="danger" className="studio-tag">
            {say('eq.design.notInProfile')}
          </Chip>
        )}
        <span className="studio-tile__state">
          <span className="studio-state" data-state={!layout.published ? 'new' : edited ? 'draft' : 'live'}>
            {!layout.published
              ? say('designs.page.neverPublished')
              : edited
                ? say('designs.page.unpublished')
                : say('designs.page.publishedAt', { when: when(layout.publishedAt, lang) })}
          </span>
          {/* Said before the design is opened: "this one cannot be published" is the
              strongest answer to which one to work on. */}
          {faults > 0 && (
            <Chip size="sm" variant="soft" color="danger">
              {say('designs.page.toFix', { n: faults })}
            </Chip>
          )}
        </span>
      </span>
    </Card>
  )
}

/** A value that has stopped changing for ms. */
function useSettled<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), ms)
    return () => window.clearTimeout(timer)
  }, [value, ms])
  return settled
}

/** A design's picture in a list; a counter display plays its screen change while hovered. */
function DesignThumb({ kind, document, bindings, playing }: { kind: LayoutKind; document: LayoutDocument; bindings: LayoutBindings; playing: boolean }) {
  return (
    <span className="studio-thumb" data-kind={kind} inert>
      {kind === 'counter' ? (
        <CounterLoop document={document} bindings={bindings} playing={playing} />
      ) : (
        <LayoutSurface document={document} screen={firstScreen(kind)} bindings={bindings} />
      )}
    </span>
  )
}

function when(iso: string | undefined, lang: StaffLanguage): string {
  if (!iso || Number.isNaN(new Date(iso).getTime())) return ''
  return formatMoment(iso, lang, { date: true, time: true })
}

// ---------------------------------------------------------------- editor

function layerName(el: LayoutElement, say: Say, lang: StaffLanguage, doc: LayoutDocument, catalogue?: Service[]): string {
  const short = (words: string) => (words.length > 28 ? words.slice(0, 27) + '…' : words)
  // In the reader's language; the canvas switch is for the canvas.
  if (el.kind === 'text' || el.kind === 'button') {
    const words = textIn(el.content, lang).trim()
    if (words) return short(words)
  }
  // A button left to say its service's or page's name is listed by that name, so
  // ten buttons are not ten lines of "Button".
  if (el.kind === 'button' && el.action) {
    if (el.action.kind === 'back') return say('designs.page.back')
    if (el.action.kind === 'page') return short(pageLabel(doc, el.action.pageId, lang))
    const id = el.action.kind === 'service' ? el.action.serviceId : ''
    const s = catalogue?.find((c) => c.id === id)
    if (s) return short(serviceName(s.name, lang, s.code))
  }
  return elementName(el, doc, lang)
}

function EditorView({
  current,
  doc,
  selected,
  picked,
  onPick,
  previewing,
  onPreviewing: setPreviewing,
  screen,
  page: pageAsked,
  branches,
  profileName,
  initialBranch,
  homeBranches,
  language,
  save,
  error,
  showProblems,
  usage,
  links,
  onNotify,
  modules,
  branchName,
  catalogue,
  canUndo,
  canRedo,
  onBack,
  onSave,
  onUndo,
  onRedo,
  onScreen: pickScreen,
  onLanguage,
  onShowProblems,
  onHistory,
  onPublish,
  onUse,
  onDelete,
  onDuplicate,
  onRename,
  duplicating,
  onSelect,
  onChange,
  onCommit,
  onRestack,
  onRemove,
  onDuplicateElements,
  onCopyElements,
  onPaste,
  onAddMediaItem,
  onSetPicture,
  moved,
  onReload,
  frozen,
}: {
  /** The design is being left: it takes no edits. */
  frozen: boolean
  current: StoredLayout
  doc: LayoutDocument
  selected: LayoutElement | null
  /** Everything selected; selected is set only when it is one element. */
  picked: LayoutElement[]
  onPick: (ids: string[]) => void
  /** A kiosk design tried as a customer at one branch would meet it. */
  previewing: boolean
  onPreviewing: (on: boolean | ((was: boolean) => boolean)) => void
  screen: Screen
  page: string
  /** For a kiosk design, its operation profile's branches. */
  branches: Branch[]
  /** A kiosk design's operation profile; empty for other kinds. */
  profileName: string
  /** The branch the design list was narrowed to, which the preview starts at. */
  initialBranch?: string
  /** The branches the person works in, after the list's branch and the design's own. */
  homeBranches?: readonly string[]
  language: string
  save: SaveStatus
  error: string
  showProblems: boolean
  usage: Usage
  /** The machines' links to offer; null hides Link (a ticket, or no right to issue keys). */
  links: LinkRow[] | null
  onNotify: (text: string) => void
  modules?: readonly string[]
  branchName: string
  /** For a kiosk design, its profile's services only. */
  catalogue?: Service[]
  canUndo: boolean
  canRedo: boolean
  onBack: () => void
  /** Saves now; a refused save is tried again. */
  onSave: () => void
  onUndo: () => void
  onRedo: () => void
  onScreen: (s: Screen, page?: string) => void
  onLanguage: (l: string) => void
  onShowProblems: (open: boolean) => void
  onHistory: () => void
  onPublish: () => void
  onUse: () => void
  onDelete: () => void
  onDuplicate: () => void
  /** The refusal to show, or null once renamed. */
  onRename: (name: string) => Promise<string | null>
  duplicating: boolean
  onSelect: (id: string | null) => void
  onChange: (doc: LayoutDocument) => void
  onCommit: (doc: LayoutDocument, before?: LayoutDocument) => void
  onRestack: (id: string, dir: 1 | -1) => void
  onRemove: (id: string) => void
  onDuplicateElements: (ids: readonly string[]) => void
  onCopyElements: (ids: readonly string[]) => void
  onPaste: () => void
  onAddMediaItem: (elementId: string, target: MediaTarget, item: MediaItem) => void
  onSetPicture: (elementId: string, assetId: string) => void
  /** The design was saved somewhere else since it was opened; nothing more is saved. */
  moved: boolean
  onReload: () => void
}) {
  const { say, lang } = useStaffText()
  const [renamingTitle, setRenamingTitle] = useState(false)
  useEffect(() => setRenamingTitle(false), [current.id])
  // Worked out once the document settles, so a drag across another box does not
  // make the badge blink on and off.
  const settled = useSettled(doc, 250)
  // The design is drawn in each customer language in turn; a language the organisation dropped is not.
  const customerLanguages = useCustomerLanguages()
  useEffect(() => {
    if (!customerLanguages.includes(language)) onLanguage(customerLanguages[0]!)
  }, [customerLanguages, language, onLanguage])
  const overflows = useMemo(() => {
    const out = new Map<string, string[]>()
    for (const el of settled.elements) {
      const over = overflowingLanguages(el, settled, customerLanguages)
      if (over.length > 0) out.set(el.id, over)
    }
    return out
  }, [settled, customerLanguages])
  const faults = useMemo(() => faultsOf(settled, lang), [settled, lang])
  const [previewBranch, setPreviewBranch] = useState<string | undefined>()
  // The same branch names the preview's header in both modes (the preview's choice, else the first).
  // Chosen in the preview; else the list's branch, the first that uses it, the person's own; else the first.
  const shownBranchId = pickBranch(branches, {}, previewBranch, initialBranch, usage.branches[0]?.id, ...(homeBranches ?? []))
  const shownBranch = doc.kind === 'kiosk' ? branches.find((b) => b.id === shownBranchId)?.name ?? branchName : branchName
  // A kiosk's branch offer, known in both modes: what it misses or hides is an issue.
  const offered = useBranchOffer(doc.kind === 'kiosk' ? shownBranchId : undefined)
  // Everything to fix, in the header's list only: the canvas stays clean.
  const issues = useMemo(
    () =>
      collectIssues({
        doc: settled,
        faults,
        warnings: warningFaults(settled, lang),
        overflows,
        nameOf: (el) => layerName(el, say, lang, settled, catalogue),
        lang,
        branch: offered ? { name: shownBranch, offered } : undefined,
      }),
    [settled, faults, overflows, say, lang, catalogue, offered, shownBranch],
  )
  const issueOf = useMemo(() => issueByElement(issues), [issues])
  // A design opens with its properties at the top, not where the last one was left.
  const inspRef = useRef<HTMLDivElement>(null)
  const toolsRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    inspRef.current?.scrollTo(0, 0)
    toolsRef.current?.scrollTo(0, 0)
  }, [current.id])
  // Whether the editor holds anything the screens have not got.
  //
  // Measured against what is published, not against what was last saved: the draft
  // is saved continuously and on its own, so "saved" says nothing a person cares
  // about. The question Publish answers is whether the wall is out of date, and a
  // design that has never been published is always out of date.
  const unpublished = !current.published || !sameDocument(doc, current.published)
  const blocked = publishBlock(save, unpublished, faults.length > 0)
  const savedLine =
    save === 'saving' ? say('designs.page.saving') : save === 'notSaved' ? say('designs.page.unsaved') : error || say(save === 'saved' ? 'designs.page.saved' : 'common.save')
  // The screen drawn on, for a kiosk, a counter display or a main display; undefined for one-screen kinds.
  const drawn = drawnScreen(doc, screen)
  // A page deleted or undone away falls back to the menu rather than to nothing.
  const page = pageIds(doc).includes(pageAsked) ? pageAsked : pageIds(doc)[0]!
  const kioskPage = drawn === 'choosing' ? page : undefined
  const visible = drawOrder(doc.elements).filter((el) => onScreen(el, drawn, kioskPage))
  const clip = useClip()
  const [zoom, setZoomState] = useState<Zoom>(() => loadZoom(current.kind))
  const [fitScale, setFitScale] = useState(1)
  useEffect(() => setZoomState(loadZoom(current.kind)), [current.kind])
  // Kept a moment after the last change, not on every step of a pinch.
  const zoomSave = useRef(0)
  const setZoom = useCallback(
    (z: Zoom) => {
      setZoomState(z)
      window.clearTimeout(zoomSave.current)
      zoomSave.current = window.setTimeout(() => saveZoom(current.kind, z), 300)
    },
    [current.kind],
  )
  const scaleNow = zoom === 'fit' ? fitScale : zoom
  const layers = [...visible].reverse()
  const n = usage.machines.length
  const k = KIND[current.kind]
  const openFromMenu = (pageId: string) => {
    const added = addOpener(doc, pageId)
    onCommit(added.doc)
    setPreviewing(false)
    pickScreen('choosing', pageIds(doc)[0])
    onSelect(added.buttonId)
  }
  // An issue picked in the list selects its element, on its own kiosk screen.
  const pickIssue = (issue: Issue) => {
    const el = doc.elements.find((e) => e.id === issue.ids[0])
    if (!el) return
    setPreviewing(false)
    const where = whereIs(el, doc, drawn ?? screen, page)
    if (where.screen !== screen || where.page !== page) pickScreen(where.screen, where.page)
    onSelect(el.id)
  }


  return (
    <main className="studio-editor" inert={frozen} aria-busy={frozen || undefined}>
      <div className="studio-edbar">
        <Button variant="ghost" size="sm" className="studio-edbar__back" onPress={onBack}>
          <span className="studio-flip">
            <Icon name="chevron" size={16} />
          </span>
          {say(k.one)}
        </Button>
        <div className="studio-edbar__title">
          <h1 title={nameOf(current)}>
            {renamingTitle ? (
              <NameField name={nameOf(current)} onRename={onRename} onDone={() => setRenamingTitle(false)} />
            ) : (
              <button type="button" className="studio-edbar__name" onClick={() => setRenamingTitle(true)}>
                {nameOf(current)}
              </button>
            )}
            {current.isDefault && (
              <Chip size="sm" variant="soft" color="accent" className="studio-tag">
                {say('designs.page.default')}
              </Chip>
            )}
            {profileName && (
              <Chip size="sm" variant="soft" className="studio-tag">
                {profileName}
              </Chip>
            )}
          </h1>
          <p>
            {usage.known !== false && (
              <button type="button" className="studio-edbar__uses" onClick={onUse}>
                {usageLine(usage, current.kind, say)}
              </button>
            )}
            {usage.known !== false && <span aria-hidden="true">·</span>}
            {/* Said out loud, because a dead Publish button with no explanation
                reads as a broken page rather than as a finished one. */}
            <span className="studio-state" data-state={!current.published ? 'new' : unpublished ? 'draft' : 'live'}>
              {!current.published ? (
                say('designs.page.neverPublished')
              ) : unpublished ? (
                <span>
                  <span className="studio-edbar__draft">{say('designs.page.draft')}</span>
                  {say('designs.page.draftLive', { version: current.version })}
                </span>
              ) : (
                say('designs.page.versionLive', { version: current.version })
              )}
            </span>
            {current.published && current.kind !== 'ticket' && usage.known !== false && usage.machines.length > 0 && (
              <ReachLine layoutId={current.id} version={current.version} machines={usage.machines} branches={branches} />
            )}
          </p>
        </div>
        <div className="studio-edbar__tools">
          <IssuesIndicator issues={issues} open={showProblems} onOpen={onShowProblems} onPick={pickIssue} onOpenPage={openFromMenu} />
          {/* The one place a refusal shows: nothing floats over the canvas or moves it.
              Every edit saves itself; pressed, it saves now (or tries again). */}
          <span role="status" aria-live="polite">
            <button
              type="button"
              className="studio-saved"
              data-state={save}
              data-tone={save === 'notSaved' || (error && save !== 'saving') ? 'bad' : undefined}
              title={error || undefined}
              disabled={save === 'saving'}
              onClick={onSave}
            >
              <span className="studio-saved__text">{savedLine}</span>
            </button>
          </span>
          {moved && (
            <Button variant="secondary" size="sm" onPress={onReload}>
              {say('common.reload')}
            </Button>
          )}
          <span className="studio-history">
            <IconButton onPress={onUndo} isDisabled={!canUndo} aria-label={say('designs.page.undo')}>
              <Icon name="undo" size={16} />
            </IconButton>
            <IconButton className="studio-redo" onPress={onRedo} isDisabled={!canRedo} aria-label={say('designs.page.redo')}>
              <Icon name="undo" size={16} />
            </IconButton>
          </span>
          <span className="studio-edbar__sep" aria-hidden="true" />
          {((doc.kind === 'kiosk' && branches.length > 0) || doc.kind === 'counter' || doc.kind === 'display') && (
            <ToggleButton
              size="sm"
              className="studio-previewtoggle"
              isSelected={previewing}
              onChange={() => {
                setPreviewing((v) => !v)
                onSelect(null)
              }}
            >
              <Icon name={previewing ? 'edit' : 'play'} size={14} /> {say(previewing ? 'designs.page.edit' : 'designs.page.preview')}
            </ToggleButton>
          )}
          {links && <MachineLinks rows={links} known={usage.known !== false} onUse={onUse} onNotify={onNotify} />}
          <Button variant="secondary" size="sm" onPress={onUse}>
            {say('eq.design.use')}
          </Button>
          {/* The reason on the wrapper, which a disabled button's hover reaches in every browser. */}
          <span className="studio-publish" title={blocked ? say(PUBLISH_BLOCK[blocked]) : undefined}>
            <Button variant="primary" size="sm" isDisabled={blocked !== null} aria-describedby={blocked ? 'studio-publish-why' : undefined} onPress={onPublish}>
              {current.kind !== 'ticket' && n > 0 ? say('designs.page.publishTo', { what: say(k.machines, { n }) }) : say('designs.page.publish')}
            </Button>
            {blocked && (
              <span id="studio-publish-why" className="sr-only">
                {say(PUBLISH_BLOCK[blocked])}
              </span>
            )}
          </span>
          <MoreMenu
            items={[
              { label: say('designs.page.duplicate'), icon: 'copy', onClick: onDuplicate, disabled: duplicating },
              { label: say('designs.page.rename'), icon: 'edit', onClick: () => setRenamingTitle(true) },
              { label: say('designs.page.history'), icon: 'clock', onClick: onHistory },
              { label: say('common.delete'), icon: 'trash', onClick: onDelete, danger: true },
            ]}
          />
        </div>
      </div>

      <div className="studio-edbody" data-previewing={previewing || undefined}>
        {!previewing && (
          <aside className="studio-tools" aria-label={say('designs.tools.label')}>
            <Card className="studio-card">
              <div className="studio-card__scroll" ref={toolsRef}>
                <LeftPanel
                  screens={
                    // Pages are edited, not previewed: the preview walks them itself.
                    doc.kind === 'counter' ? (
                      <CounterTabs screen={drawn ?? 'called'} onPick={(s) => pickScreen(s)} />
                    ) : doc.kind === 'display' ? (
                      <DisplayTabs
                        document={doc}
                        screen={drawn === 'call' ? 'call' : 'board'}
                        onPick={(s) => pickScreen(s)}
                        onAdd={() => {
                          onCommit(addCalled(doc))
                          pickScreen('call')
                        }}
                      />
                    ) : doc.kind === 'kiosk' ? (
                      <PageTabs
                        document={doc}
                        screen={screen as KioskScreen}
                        page={page}
                        onPick={pickScreen}
                        onAddPage={() => {
                          const added = addPage(doc)
                          onCommit(added.doc)
                          pickScreen('choosing', added.pageId)
                        }}
                      />
                    ) : undefined
                  }
                  layers={
                    <LayerList
                      rows={layers.map((el) => ({
                        element: el,
                        name: layerName(el, say, lang, doc, catalogue),
                        kind: elementName(el, doc, lang),
                        issue: issueOf.get(el.id),
                      }))}
                      picked={picked.map((p) => p.id)}
                      onSelect={onSelect}
                      onShiftSelect={(id) => onPick(toggleIn(picked.map((p) => p.id), id))}
                      onRestack={onRestack}
                      onToggleHidden={(id) => onCommit(toggleHidden(doc, id))}
                    />
                  }
                >
                  {(query) => (
                    <EditorPalette
                      document={doc}
                      modules={modules}
                      screen={drawn}
                      page={kioskPage}
                      services={catalogue}
                      query={query}
                      onAdd={(element) => {
                        onCommit({ ...doc, elements: [...doc.elements, element] })
                        onSelect(element.id)
                      }}
                    />
                  )}
                </LeftPanel>
              </div>
            </Card>
          </aside>
        )}

        <section className="studio-stagewrap" aria-label={say('designs.page.preview')}>
          <div className="studio-stage" data-kind={doc.kind}>
            {previewing && doc.kind === 'kiosk' && shownBranchId ? (
              <KioskPreview
                document={doc}
                branches={branches}
                branchId={shownBranchId}
                offered={offered}
                language={language}
                languages={customerLanguages}
                assetURL={api.assetURL}
                onBranch={setPreviewBranch}
              />
            ) : previewing && doc.kind === 'counter' ? (
              <CounterPreview document={doc} branchName={shownBranch} language={language} assetURL={api.assetURL} />
            ) : previewing && doc.kind === 'display' ? (
              <DisplayPreview document={doc} branchName={shownBranch} language={language} languages={customerLanguages} assetURL={api.assetURL} />
            ) : (
            <EditorCanvas
              document={doc}
              screen={drawn}
              page={kioskPage}
              selectedIds={picked.map((el) => el.id)}
              onSelect={onSelect}
              onToggle={(id) => onPick(toggleIn(picked.map((el) => el.id), id))}
              onChange={onChange}
              onCommit={onCommit}
              assetURL={api.assetURL}
              branchName={shownBranch}
              services={catalogue}
              language={language}
              languages={customerLanguages}
              zoom={zoom}
              onZoom={setZoom}
              onFitScale={setFitScale}
            />
            )}
          </div>
          <div className="studio-stagefoot">
            <span className="studio-stagecap">
              {doc.canvas.width} × {doc.canvas.height} {say(doc.kind === 'ticket' ? 'designs.page.dots' : 'designs.page.px')}
            </span>
            <span className="studio-spacer" />
            {customerLanguages.length > 1 && (
              <ToggleButtonGroup
                size="sm"
                selectionMode="single"
                disallowEmptySelection
                aria-label={say('designs.page.previewLanguage')}
                selectedKeys={new Set([language])}
                onSelectionChange={(keys) => {
                  const next = [...keys][0]
                  if (next !== undefined) onLanguage(String(next))
                }}
              >
                {customerLanguages.map((l) => (
                  <ToggleButton key={l} id={l} aria-label={nativeName(l)}>
                    {l.toUpperCase()}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
            {!previewing && (
              <Button variant="secondary" size="sm" isDisabled={!clip} onPress={onPaste}>
                {say('designs.clip.paste')}
              </Button>
            )}
            {!previewing && (
              <div className="studio-zoom" role="group" aria-label={say('designs.zoom.label')}>
                <IconButton onPress={() => setZoom(stepZoom(scaleNow, -1))} aria-label={say('designs.zoom.out')}>
                  −
                </IconButton>
                <span className="studio-zoom__n" aria-live="polite">
                  {Math.round(scaleNow * 100)}%
                </span>
                <IconButton onPress={() => setZoom(stepZoom(scaleNow, 1))} aria-label={say('designs.zoom.in')}>
                  <Icon name="plus" size={14} />
                </IconButton>
                <ToggleButtonGroup
                  size="sm"
                  selectionMode="single"
                  aria-label={say('designs.zoom.label')}
                  selectedKeys={new Set(zoom === 'fit' ? ['fit'] : zoom === 1 ? ['actual'] : [])}
                  onSelectionChange={(keys) => {
                    const next = [...keys][0]
                    if (next === 'fit') setZoom('fit')
                    else if (next === 'actual') setZoom(1)
                  }}
                >
                  <ToggleButton id="fit">{say('designs.zoom.fit')}</ToggleButton>
                  <ToggleButton id="actual">100%</ToggleButton>
                </ToggleButtonGroup>
              </div>
            )}
          </div>
        </section>

        {!previewing && (
        <aside className="studio-insp" aria-label={say('designs.page.properties')}>
          <Card className="studio-card">
            <div className="studio-card__scroll" ref={inspRef}>
              {picked.length > 1 ? (
                <AlignPanel
                  n={picked.length}
                  onAlign={(how) => onCommit(align(doc, picked.map((el) => el.id), how))}
                  onDistribute={(axis) => onCommit(distribute(doc, picked.map((el) => el.id), axis))}
                  onDuplicate={() => onDuplicateElements(picked.map((el) => el.id))}
                  onCopy={() => onCopyElements(picked.map((el) => el.id))}
                  onDelete={() => {
                    onPick([])
                    onCommit(removeMany(doc, picked.map((el) => el.id)))
                  }}
                />
              ) : !selected ? (
                <DesignPanel
                  document={doc}
                  layoutId={current.id}
                  onChangeCanvas={(canvas) => onCommit({ ...doc, canvas })}
                  onResize={(width, height) => onCommit(resizeCanvas(doc, width, height))}
                  machines={usage.machines}
                  allBranches={current.isDefault === true}
                  usageKnown={usage.known !== false}
                >
                  {doc.kind === 'kiosk' && kioskPage && <PagePanel key={kioskPage} document={doc} page={kioskPage} onReplace={onCommit} />}
                  {doc.kind === 'kiosk' && drawn === 'attract' && <WelcomePanel document={doc} onReplace={onCommit} />}
                  {doc.kind === 'display' && drawn === 'call' && <CalledPanel document={doc} onReplace={onCommit} />}
                </DesignPanel>
              ) : (
              <Inspector
                key={selected.id}
                document={doc}
                element={selected}
                services={catalogue}
                language={language}
                onChange={(element) => onCommit({ ...doc, elements: doc.elements.map((el) => (el.id === element.id ? element : el)) })}
                onReplace={onCommit}
                // Bound to the design open at this render, which is the render an
                // upload starts from: a finished upload names the design it began in.
                onAddMediaItem={onAddMediaItem}
                onSetPicture={onSetPicture}
                onDelete={onRemove}
                onDuplicate={(id) => onDuplicateElements([id])}
                onCopy={(id) => onCopyElements([id])}
                onDone={() => onSelect(null)}
              />
              )}
            </div>
          </Card>
        </aside>
        )}
      </div>
    </main>
  )
}

// ---------------------------------------------------------------- publish and history

function PublishDialog({
  name,
  kind,
  faults,
  usage,
  branches,
  onClose,
  onPublish,
}: {
  name: string
  kind: LayoutKind
  faults: string[]
  usage: Usage
  branches: Branch[]
  onClose: () => void
  onPublish: (note: string) => void
}) {
  const { say } = useStaffText()
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  // The branches that use it, the single machines that chose it over their
  // branch, and a slip's services.
  const where = [
    ...usage.branches.map((b) => ({ id: b.id, name: b.name, note: say('designs.page.branch') })),
    ...usage.ownMachines.map((m) => ({ id: m.id, name: m.name, note: branches.find((b) => b.id === m.branchId)?.name ?? '' })),
    ...usage.services.map((s) => ({ id: s.id, name: s.name.en || s.name.az || s.code, note: say('designs.page.service') })),
  ]

  if (faults.length > 0) {
    return (
      <StudioModal
        title={say('designs.page.fixFirst')}
        onClose={onClose}
        footer={
          <Button variant="primary" onPress={onClose}>
            {say('designs.page.close')}
          </Button>
        }
      >
        <ul className="studio-problems">
          {faults.map((f, i) => (
            <li key={`${i}:${f}`}>{f}</li>
          ))}
        </ul>
      </StudioModal>
    )
  }
  return (
    <StudioModal
      title={say('designs.page.publishName', { name })}
      onClose={onClose}
      onSubmit={() => {
        if (busy) return
        setBusy(true)
        onPublish(note.trim())
      }}
      footer={
        <>
          <Button variant="tertiary" onPress={onClose}>
            {say('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" isPending={busy}>
            {say('designs.page.publish')}
          </Button>
        </>
      }
    >
      {where.length > 0 ? (
        <>
          <p className="studio-dialog__lead">{say(kind === 'ticket' ? 'designs.page.printedFor' : 'designs.page.shownOn')}</p>
          <ul className="studio-dialog__list">
            {where.map((w) => (
              <li key={w.id}>
                {w.name}
                <span className="studio-muted">{w.note}</span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        usage.known && <p className="studio-dialog__lead">{say('designs.page.noMachine')}</p>
      )}
      <TextField className="studio-field" autoFocus value={note} maxLength={200} onChange={setNote}>
        <Label>{say('designs.page.whatChanged')}</Label>
        <Input variant="secondary" placeholder={say('designs.page.whatChangedSample')} />
      </TextField>
    </StudioModal>
  )
}

function HistoryDrawer({
  kind,
  doc,
  history,
  branchName,
  services,
  onClose,
  onRestore,
}: {
  kind: LayoutKind
  doc: LayoutDocument
  history: LayoutVersion[]
  branchName: string
  services?: Service[]
  onClose: () => void
  onRestore: (v: LayoutVersion) => void
}) {
  const { say, lang } = useStaffText()
  return (
    <StudioModal
      size="lg"
      title={say('designs.page.history')}
      onClose={onClose}
      footer={
        <Button variant="tertiary" onPress={onClose}>
          {say('ui.close')}
        </Button>
      }
    >
      {history.length === 0 ? (
        <p className="studio-dialog__lead">{say('designs.page.notPublishedYet')}</p>
      ) : (
        <ol className="studio-versions">
          {history.map((v, i) => {
            // The version already in the editor has nothing to bring back.
            const inDraft = sameDocument(v.document, doc)
            return (
              <li key={v.version} className="studio-version">
                <span className="studio-version__stage" data-kind={kind}>
                  <span className="studio-thumb" data-kind={kind} inert>
                    <LayoutSurface
                      document={v.document}
                      screen={firstScreen(kind)}
                      bindings={previewBindings(v.document, branchName, services, api.assetURL)}
                    />
                  </span>
                </span>
                <span className="studio-version__main">
                  <strong>
                    {say('designs.page.version', { version: v.version })}
                    {v.note ? `: ${v.note}` : ''}
                  </strong>
                  <span className="studio-muted">
                    {when(v.publishedAt, lang)}
                    {v.publishedBy ? `, ${v.publishedBy}` : ''}
                  </span>
                  {i === 0 && (
                    <Chip size="sm" variant="soft" color="success" className="studio-version__live">
                      {say('designs.page.live')}
                    </Chip>
                  )}
                </span>
                {/* Into the draft, never onto the wall: Publish is still the only
                    way anything reaches a screen. */}
                {inDraft ? (
                  <Chip size="sm" variant="soft">
                    {say('designs.page.inDraft')}
                  </Chip>
                ) : (
                  <Button variant="secondary" size="sm" onPress={() => onRestore(v)}>
                    {say('designs.page.restore')}
                  </Button>
                )}
              </li>
            )
          })}
        </ol>
      )}
    </StudioModal>
  )
}

// ---------------------------------------------------------------- new design

/**
 * Starting a design: what it is for, what it is called, and what to start from.
 * A kiosk design also belongs to an operation profile, whose services it offers.
 *
 * What from: the server's designs, drawn rather than described, and the
 * organisation's own designs of the same kind, which is how one is copied.
 */
function NewDesign({
  initialKind,
  layouts,
  templates,
  branchName,
  services,
  profiles,
  profileDocs,
  onClose,
  onCreate,
}: {
  initialKind: LayoutKind
  layouts: StoredLayout[]
  templates: Record<LayoutKind, LayoutTemplate[]> | null
  branchName: string
  services?: Service[]
  profiles: OperationProfileRow[]
  profileDocs: ProfileDocs
  onClose: () => void
  onCreate: (of: {
    kind: LayoutKind
    template?: string
    copyOf?: StoredLayout
    name: string
    document?: LayoutDocument
    made?: LayoutDocument
    operationProfileId?: string
  }) => Promise<string | null>
}) {
  const { say, lang } = useStaffText()
  const [kind, setKind] = useState<LayoutKind>(initialKind)
  const [name, setName] = useState('')
  const [from, setFrom] = useState('')
  const [hoveredStart, setHoveredStart] = useState('')
  const [busy, setBusy] = useState(false)
  const [refused, setRefused] = useState('')
  // A kiosk is made for an operation profile and a screen: its buttons are that
  // profile's services, laid out for that panel. The first profile until one is picked.
  const [profilePicked, setProfilePicked] = useState('')
  const profileId = profiles.some((p) => p.id === profilePicked) ? profilePicked : profiles[0]?.id ?? ''
  // The panel a kiosk or a counter display is drawn for; the kind's first until one is picked.
  const [sizePicked, setSizePicked] = useState('')
  const sizes = presetsFor(kind === 'counter' ? 'counter' : 'kiosk')
  const size = sizes.find((p) => `${p.width}x${p.height}` === sizePicked) ?? sizes[0]!
  const profileDoc = profileDocs?.[profileId]
  // A kiosk start is drawn from its profile's services: one drawn before they load
  // offered the whole catalogue, and its first save was refused (422).
  const kioskWaiting = kind === 'kiosk' && (services === undefined || profileDocs === null)
  const offered = useMemo(
    () => (kind === 'kiosk' && services && profileDoc ? servicesOfProfile(services, profileDoc) : null),
    [kind, services, profileDoc],
  )

  const starts = useMemo(() => {
    // A kiosk copies only designs of its own profile: another's may name services this one lacks.
    const own = layouts.filter((l) => l.kind === kind && (kind !== 'kiosk' || l.operationProfileId === profileId))
    const made =
      kind === 'kiosk' && offered && offered.length > 0
        ? KIOSK_LOOKS.map(({ look, label }) => ({
            key: `k:${look}`,
            label: say(label),
            note: '',
            document: kioskFromServices(offered, size, look as KioskLook),
            template: undefined as string | undefined,
            copyOf: undefined as StoredLayout | undefined,
            made: true,
            resized: false,
          }))
        : []
    // Every kiosk and counter start is drawn for the chosen screen, not only the made ones.
    const sized = (d: LayoutDocument) => (kind === 'kiosk' || kind === 'counter' ? atSize(d, size) : d)
    // A counter display starts from each look (Simple, Signal, Big number) at that size ({look}-{w}x{h}).
    const counterLooks = (kind === 'counter' ? counterStarts(templates?.counter ?? [], size, atSize) : []).map((c) => ({
      key: `t:${c.template.name}`, label: c.template.label, note: '', document: c.document, template: c.template.name as string | undefined, copyOf: undefined as StoredLayout | undefined, made: false, resized: c.document !== c.template.document,
    }))
    // A kiosk template starts with a button per profile service where its grid was;
    // with no profile services known it keeps the grid, which chooses none.
    const started = (d: LayoutDocument) =>
      kind !== 'kiosk' ? d : offered && offered.length > 0 ? withoutGrids(sized(d), offered) : sized(d)
    return [
      ...made,
      ...counterLooks,
      ...(kind === 'counter' ? [] : templates?.[kind] ?? []).map((t) => {
        const start = started(t.document)
        return { key: `t:${t.name}`, label: kind === 'kiosk' ? say('designs.page.simple') : templateLabel(t, lang), note: '', document: start, template: t.name, copyOf: undefined as StoredLayout | undefined, made: false, resized: start !== t.document }
      }),
      ...own.map((l) => ({ key: `d:${l.id}`, label: nameOf(l), note: say('designs.page.copy'), document: sized(l.draft), template: undefined, copyOf: l, made: false, resized: sized(l.draft) !== l.draft })),
    ]
  }, [templates, layouts, kind, offered, size, say, profileId])
  const source = starts.find((s) => s.key === from) ?? starts[0]

  // Names tell designs apart within a kind; the server refuses a second one the
  // same, and said here the name can be changed before anything is sent.
  const trimmed = name.trim()
  const clash = trimmed !== '' && layouts.some((l) => l.kind === kind && nameOf(l).trim().toLowerCase() === trimmed.toLowerCase())
  const valid = !!source && !kioskWaiting && trimmed !== '' && !clash && (kind !== 'kiosk' || profileId !== '')

  const submit = async () => {
    if (!source) return
    setBusy(true)
    setRefused('')
    const answer = await onCreate({
      kind,
      template: source.template,
      copyOf: source.copyOf,
      name: trimmed,
      // A template is saved over only when it was redrawn: for another screen, or with service buttons.
      document: source.copyOf || source.resized ? source.document : undefined,
      made: source.made ? source.document : undefined,
      operationProfileId: kind === 'kiosk' ? profileId : undefined,
    })
    setBusy(false)
    if (answer) setRefused(answer)
  }

  const sizeField = (
    <StudioSelect
      label={say('designs.page.screen')}
      value={`${size.width}x${size.height}`}
      onChange={setSizePicked}
      options={sizes.map((p) => ({ value: `${p.width}x${p.height}`, label: presetLabel(p, lang) }))}
    />
  )

  return (
    <StudioModal
      size="lg"
      placement="top"
      guard
      title={say('designs.page.new')}
      onClose={onClose}
      onSubmit={() => valid && !busy && void submit()}
      footer={
        <>
          <Button variant="tertiary" onPress={onClose}>
            {say('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" isDisabled={!valid} isPending={busy}>
            {say('designs.page.create')}
          </Button>
        </>
      }
    >
      {refused && <Notice>{refused}</Notice>}
      <fieldset className="studio-field">
        <legend className="studio-field__label">{say('designs.page.for')}</legend>
        <Seg
          label={say('designs.page.for')}
          value={kind}
          options={KINDS.map((x) => [x, say(KIND[x].one)])}
          onChange={(x) => {
            setKind(x as LayoutKind)
            setFrom('')
          }}
        />
      </fieldset>

      <TextField className="studio-field" value={name} maxLength={MAX_DESIGN_NAME} isInvalid={clash} onChange={setName}>
        <Label>{say('designs.page.name')}</Label>
        <Input
          variant="secondary"
          placeholder={say(kind === 'ticket' ? 'designs.page.sampleTicket' : kind === 'kiosk' ? 'designs.page.sampleKiosk' : kind === 'counter' ? 'designs.page.sampleCounter' : 'designs.page.sampleDisplay')}
        />
        {clash && <small className="studio-field__error">{say('designs.page.nameTaken')}</small>}
      </TextField>

      {kind === 'kiosk' && (
        <div className="studio-row2">
          {profiles.length === 0 ? (
            <div className="studio-field">
              <span className="studio-field__label">{say('eq.design.profile')}</span>
              <small className="studio-field__error">{say('eq.design.noProfiles')}</small>
            </div>
          ) : (
            <StudioSelect
              label={say('eq.design.profile')}
              value={profileId}
              onChange={(value) => {
                setProfilePicked(value)
                setFrom('')
              }}
              options={profiles.map((p) => ({ value: p.id, label: p.name }))}
            />
          )}
          {sizeField}
        </div>
      )}
      {kind === 'counter' && sizeField}

      <fieldset className="studio-field">
        <legend className="studio-field__label">{say('designs.page.startFrom')}</legend>
        {!templates || kioskWaiting ? (
          <small className="studio-field__hint">{say('common.loading')}</small>
        ) : (
          <div className="studio-starts">
            {starts.map((s) => (
              <label
                key={s.key}
                className="studio-start"
                data-on={source?.key === s.key || undefined}
                onMouseEnter={() => setHoveredStart(s.key)}
                onMouseLeave={() => setHoveredStart('')}
              >
                <input type="radio" name="from" className="sr-only" checked={source?.key === s.key} onChange={() => setFrom(s.key)} />
                <span className="studio-start__stage">
                  <DesignThumb
                    kind={kind}
                    document={s.document}
                    bindings={previewBindings(s.document, branchName, offered ?? services, api.assetURL)}
                    playing={hoveredStart === s.key}
                  />
                </span>
                <span className="studio-start__name">{s.label}</span>
                {s.note && <span className="studio-start__note">{s.note}</span>}
              </label>
            ))}
          </div>
        )}
      </fieldset>
    </StudioModal>
  )
}

// ---------------------------------------------------------------- use

/** What Use sends: the default and, for a slip, its services. */
type UseChoice = { isDefault: boolean; serviceIds: Set<string> }

/**
 * All branches: whether a design is the default — a kiosk design for its
 * operation profile, any other for the organisation — and, for a slip, which
 * services print it. Ticked branches: every machine of the kind there chooses
 * it of its own. One Apply does it all: the draft is published first, then the
 * branches and the default are set, each live at once.
 */
function UseDrawer({
  layout,
  layouts,
  profileName,
  services,
  needsPublish,
  faulty,
  onClose,
  onPublish,
  onSave,
  onApplied,
  onDone,
}: {
  layout: StoredLayout
  layouts: StoredLayout[]
  /** A kiosk design's operation profile. */
  profileName: string
  services?: Service[]
  /** The draft differs from what screens show (or was never published). */
  needsPublish: boolean
  /** The draft has issues that stop Publish. */
  faulty: boolean
  onClose: () => void
  /** Publishes the draft; the refusal to show, or null. */
  onPublish: () => Promise<string | null>
  /** Saves the default and services; close false keeps the drawer open. */
  onSave: (a: UseChoice, close: boolean) => Promise<string | null>
  /** These branches were sent: their machines' designs may have changed. */
  onApplied: (branchIds: string[]) => void
  /** Everything applied: the drawer closes. */
  onDone: () => void
}) {
  const [saved, setSaved] = useState<UseChoice>(() => ({ isDefault: !!layout.isDefault, serviceIds: new Set(layout.serviceIds ?? []) }))
  const [isDefault, setDefault] = useState(saved.isDefault)
  const [serviceIds, setServiceIds] = useState(() => new Set(saved.serviceIds))
  const { say, lang } = useStaffText()
  const [busy, setBusy] = useState(false)
  const [refused, setRefused] = useState('')

  const [lines, setLines] = useState<DesignBranch[]>([])
  const [canApply, setCanApply] = useState(false)
  const [ticks, setTicks] = useState<Ticks>({})
  const [results, setResults] = useState<Record<string, DesignResult> | null>(null)
  const loadBranches = useCallback(async () => {
    try {
      const answer = await api.layoutBranches(layout.id)
      setLines(answer.branches)
      setCanApply(answer.canApply)
    } catch (err) {
      setRefused(explain(err, lang, 'designs.page.notSaved'))
    }
  }, [layout.id, lang])
  useEffect(() => {
    void loadBranches()
  }, [loadBranches])
  const plan = planOf(lines, ticks)
  const noun = machineNoun(layout.kind)

  // The designs that compete for the same default: a kiosk's within its profile.
  const others = layouts.filter(
    (l) => l.kind === layout.kind && l.id !== layout.id && (layout.kind !== 'kiosk' || l.operationProfileId === layout.operationProfileId),
  )
  const currentDefault = others.find((l) => l.isDefault)
  const defaultLabel = layout.kind === 'kiosk' ? say('designs.use.allOn', { profile: profileName }) : say('designs.use.all')
  const assignmentChanged =
    isDefault !== saved.isDefault ||
    (layout.kind === 'ticket' && (serviceIds.size !== saved.serviceIds.size || [...serviceIds].some((id) => !saved.serviceIds.has(id))))

  const toggle = (id: string, on: boolean) => {
    const next = new Set(serviceIds)
    if (on) next.add(id)
    else next.delete(id)
    setServiceIds(next)
  }

  const tickBranch = (b: DesignBranch) => setTicks((t) => toggleTick(b, t))

  const submit = async () => {
    setBusy(true)
    setRefused('')
    try {
      if (needsPublish) {
        const answer = await onPublish()
        if (answer) return setRefused(answer)
      }
      let applied = true
      if (hasPlan(plan)) {
        try {
          const answer = await api.applyLayoutBranches(layout.id, { apply: plan.apply, remove: plan.remove })
          setResults(Object.fromEntries(answer.results.map((r) => [r.branchId, r])))
          setTicks({})
          onApplied(answer.results.map((r) => r.branchId))
          applied = answer.results.every(succeeded)
          await loadBranches()
        } catch (err) {
          return setRefused(explain(err, lang, 'designs.page.notSaved'))
        }
      }
      if (assignmentChanged) {
        const answer = await onSave({ isDefault, serviceIds }, false)
        if (answer) return setRefused(answer)
        setSaved({ isDefault, serviceIds: new Set(serviceIds) })
      }
      // A branch that could not take it stays listed with why.
      if (applied) onDone()
    } finally {
      setBusy(false)
    }
  }

  const work = needsPublish || hasPlan(plan) || assignmentChanged
  const blocked = needsPublish && faulty
  return (
    <StudioModal
      size="lg"
      guard
      title={say('eq.design.useName', { name: nameOf(layout) })}
      onClose={onClose}
      onSubmit={() => !busy && !blocked && work && void submit()}
      footer={
        <>
          <Button variant="tertiary" onPress={onClose}>
            {!work ? say('designs.use.done') : say('common.cancel')}
          </Button>
          {work && (
            <Button type="submit" variant="primary" isDisabled={blocked} isPending={busy}>
              {say('designs.use.apply')}
            </Button>
          )}
        </>
      }
    >
      {refused && <Notice>{refused}</Notice>}
      {blocked ? (
        <Notice tone="warning">{say('designs.page.publishFaults')}</Notice>
      ) : (
        needsPublish && <Notice tone="accent">{say('designs.use.goesLive')}</Notice>
      )}
      {hasPlan(plan) && <PlanNotice plan={plan} noun={noun} />}

      <Switch className="studio-toggle" isSelected={isDefault} onChange={setDefault} aria-label={defaultLabel}>
        <Switch.Content>
          <span className="studio-toggle__label">
            <strong>{defaultLabel}</strong>
            {currentDefault && !isDefault && <small>{say('designs.page.now', { name: nameOf(currentDefault) })}</small>}
          </span>
          <Switch.Control>
            <Switch.Thumb />
          </Switch.Control>
        </Switch.Content>
      </Switch>

      {lines.length > 0 && (
        <UseBranches lines={lines} ticks={ticks} noun={noun} canApply={canApply} results={results} onToggle={tickBranch} />
      )}

      {layout.kind === 'ticket' && (services ?? []).some((s) => s.active) && (
        <details className="studio-assign" open={serviceIds.size > 0}>
          <summary>
            {say('designs.page.servicesTitle')} <span className="studio-muted">{serviceIds.size}</span>
          </summary>
          <ul className="studio-assign__list">
            {(services ?? [])
              .filter((s) => s.active)
              .map((s) => {
                const on = serviceIds.has(s.id)
                const other = others.find((l) => (l.serviceIds ?? []).includes(s.id))
                const name = s.name.en || s.name.az || s.code
                return (
                  <li key={s.id}>
                    <AssignRow tick={on ? 'on' : 'off'} disabled={false} label={name} onChange={() => toggle(s.id, !on)}>
                      <span className="studio-assign__name">{name}</span>
                      <span className="studio-muted studio-assign__code">{s.code}</span>
                      {other && (
                        <Chip size="sm" variant="soft" color={on ? 'warning' : 'default'} className="studio-assign__via">
                          {on ? say('designs.page.movesFrom', { name: nameOf(other) }) : nameOf(other)}
                        </Chip>
                      )}
                    </AssignRow>
                  </li>
                )
              })}
          </ul>
        </details>
      )}
    </StudioModal>
  )
}

// ---------------------------------------------------------------- several at once

const ALIGNS: Array<[Alignment, StaffKey]> = [
  ['left', 'designs.multi.left'],
  ['centre', 'designs.multi.centre'],
  ['right', 'designs.multi.right'],
  ['top', 'designs.multi.top'],
  ['middle', 'designs.multi.middle'],
  ['bottom', 'designs.multi.bottom'],
]

/** In the inspector's place while two or more elements are selected. */
function AlignPanel({
  n,
  onAlign,
  onDistribute,
  onDuplicate,
  onCopy,
  onDelete,
}: {
  n: number
  onAlign: (how: Alignment) => void
  onDistribute: (axis: 'x' | 'y') => void
  onDuplicate: () => void
  onCopy: () => void
  onDelete: () => void
}) {
  const { say } = useStaffText()
  return (
    <section className="studio-panel">
      <div className="studio-panel__head">
        <h2>{say('designs.multi.selected', { n })}</h2>
      </div>
      <fieldset className="studio-field">
        <legend className="studio-field__label">{say('designs.multi.align')}</legend>
        <div className="studio-align__grid">
          {ALIGNS.map(([how, key]) => (
            <Button key={how} variant="secondary" size="sm" onPress={() => onAlign(how)}>
              {say(key)}
            </Button>
          ))}
        </div>
      </fieldset>
      {n >= 3 && (
        <fieldset className="studio-field">
          <legend className="studio-field__label">{say('designs.multi.distribute')}</legend>
          <div className="studio-align__grid studio-align__grid--two">
            <Button variant="secondary" size="sm" onPress={() => onDistribute('x')}>
              {say('designs.multi.across')}
            </Button>
            <Button variant="secondary" size="sm" onPress={() => onDistribute('y')}>
              {say('designs.multi.down')}
            </Button>
          </div>
        </fieldset>
      )}
      <div className="studio-panel__acts">
        <Button variant="secondary" size="sm" onPress={onDuplicate}>
          <Icon name="copy" size={14} /> {say('designs.page.duplicate')}
        </Button>
        <Button variant="secondary" size="sm" onPress={onCopy}>
          {say('designs.clip.copy')}
        </Button>
        <Button variant="danger-soft" size="sm" onPress={onDelete}>
          <Icon name="trash" size={14} /> {say('common.delete')}
        </Button>
      </div>
    </section>
  )
}

const PUBLISH_BLOCK: Record<PublishBlock, StaffKey> = {
  saving: 'designs.page.publishWaits',
  notSaved: 'designs.page.publishNotSaved',
  faults: 'designs.page.publishFaults',
  nothingNew: 'designs.page.publishNothingNew',
}

// ---------------------------------------------------------------- rename

const NAME_PROBLEM: Record<NameProblem, StaffKey> = {
  required: 'errors.VALIDATION_FAILED.NAME_REQUIRED',
  tooLong: 'errors.VALIDATION_FAILED.NAME_TOO_LONG',
  taken: 'errors.LAYOUT_EXISTS',
}

/** The editor's title as an input: Enter or leaving it saves, Escape cancels. */
function NameField({ name, onRename, onDone }: { name: string; onRename: (name: string) => Promise<string | null>; onDone: () => void }) {
  const { say } = useStaffText()
  const [value, setValue] = useState(name)
  const [refused, setRefused] = useState('')
  const busy = useRef(false)
  const input = useRef<HTMLInputElement>(null)
  useEffect(() => input.current?.select(), [])
  const save = async (keep: boolean) => {
    if (busy.current) return
    busy.current = true
    const answer = await onRename(value)
    busy.current = false
    if (!answer) return onDone()
    setRefused(answer)
    if (keep) input.current?.focus()
  }
  return (
    <span className="studio-rename">
      <Input
        ref={input}
        variant="secondary"
        className="studio-rename__input"
        value={value}
        aria-label={say('designs.page.name')}
        aria-invalid={refused ? true : undefined}
        aria-describedby={refused ? 'studio-rename-refused' : undefined}
        onChange={(e) => {
          setValue(e.target.value)
          setRefused('')
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            void save(true)
          } else if (e.key === 'Escape') {
            e.preventDefault()
            e.stopPropagation()
            busy.current = true // the blur that follows does not save
            onDone()
          }
        }}
        onBlur={() => void save(false)}
      />
      {refused && (
        <span className="studio-rename__refused" id="studio-rename-refused" role="alert">
          {refused}
        </span>
      )}
    </span>
  )
}

function RenameDialog({ name, onClose, onRename }: { name: string; onClose: () => void; onRename: (name: string) => Promise<string | null> }) {
  const { say } = useStaffText()
  const [value, setValue] = useState(name)
  const [busy, setBusy] = useState(false)
  const [refused, setRefused] = useState('')
  const save = async () => {
    if (busy) return
    setBusy(true)
    const answer = await onRename(value)
    setBusy(false)
    if (answer) setRefused(answer)
  }
  return (
    <StudioModal
      size="sm"
      title={say('designs.page.rename')}
      onClose={onClose}
      onSubmit={() => void save()}
      footer={
        <>
          <Button variant="tertiary" onPress={onClose}>
            {say('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" isPending={busy}>
            {say('common.save')}
          </Button>
        </>
      }
    >
      <TextField
        className="studio-field"
        autoFocus
        value={value}
        aria-label={say('designs.page.name')}
        isInvalid={!!refused}
        onFocus={(e) => (e.target as HTMLInputElement).select()}
        onChange={(v) => {
          setValue(v)
          setRefused('')
        }}
      >
        <Input variant="secondary" />
      </TextField>
      {refused && <Notice>{refused}</Notice>}
    </StudioModal>
  )
}

// ---------------------------------------------------------------- delete

function DeleteDialog({ name, onClose, onDelete }: { name: string; onClose: () => void; onDelete: () => Promise<string | null> }) {
  const { say } = useStaffText()
  const [busy, setBusy] = useState(false)
  const [refused, setRefused] = useState('')
  return (
    <StudioModal
      size="sm"
      title={say('designs.page.deleteName', { name })}
      onClose={onClose}
      footer={
        <>
          <Button variant="tertiary" onPress={onClose} autoFocus>
            {say(refused ? 'designs.page.close' : 'common.cancel')}
          </Button>
          {!refused && (
            <Button
              variant="danger"
              isPending={busy}
              onPress={async () => {
                setBusy(true)
                const answer = await onDelete()
                setBusy(false)
                if (answer) setRefused(answer)
              }}
            >
              {say('common.delete')}
            </Button>
          )}
        </>
      }
    >
      {refused ? <Notice>{refused}</Notice> : <p className="studio-dialog__lead">{say('designs.page.historyGoes')}</p>}
    </StudioModal>
  )
}

/** Leaving a draft the server did not keep. */
function LeaveDialog({ onStay, onLeave }: { onStay: () => void; onLeave: () => void }) {
  const { say } = useStaffText()
  return (
    <StudioModal
      size="sm"
      title={say('designs.leave.title')}
      onClose={onStay}
      footer={
        <>
          <Button variant="tertiary" onPress={onLeave}>
            {say('designs.leave.go')}
          </Button>
          <Button variant="primary" onPress={onStay} autoFocus>
            {say('designs.leave.stay')}
          </Button>
        </>
      }
    >
      <p className="studio-dialog__lead">{say('designs.leave.lost')}</p>
    </StudioModal>
  )
}


// ---------------------------------------------------------------- names

export function mediaIncomplete(err: unknown): boolean {
  return err instanceof ApiError && err.status === 422 && err.reason === 'MEDIA_INCOMPLETE'
}

/** Whether a save was refused because the hourly cleanup deleted a film the
 *  design plays (handleSaveDraft's FILM_GONE). Publish saves first, so it can
 *  meet the same refusal. */
export function filmGone(err: unknown): boolean {
  return err instanceof ApiError && err.status === 422 && err.reason === 'FILM_GONE'
}

/** A starting design's name: the reader's words for the server's own, else its label. */
function templateLabel(tpl: LayoutTemplate, lang: StaffLanguage): string {
  const key = `designs.template.${tpl.name.replace(/-(\w)/g, (_, c: string) => c.toUpperCase())}`
  return hasPhrase(key) ? t(key as StaffKey, lang) : tpl.label
}

/** What to call a design. Every design is named since 0062; the kind is the
 *  fallback for an answer from an older server. */
function nameOf(layout: StoredLayout): string {
  return layout.name || layout.kind
}
